// Create a Quotation as a FORM.
//
// WHY THIS EXISTS ALONGSIDE THE DOCUMENT STUDIO. The Studio
// (RbmDocumentEditorPage) is a WYSIWYG print sheet: you edit the page that gets
// printed. That is excellent for seeing the output and hopeless for capturing
// data, because a field that is not drawn on the sheet has nowhere to live —
// which is exactly why the client's address and GSTIN, carefully entered in
// Contacts, never reached a quotation. This page captures the data; the Studio
// stays reachable from the link in the header for anyone who wants the sheet.
//
// THREE THINGS THIS PAGE DOES THAT THE STUDIO DOES NOT:
//
//  1. Bill To is LOCKED to the CRM Contact. Address and GSTIN are read from the
//     Contact and snapshotted onto the document by the server (which derives
//     them itself — see POST /api/sales-ops/orders — so the lock is real and
//     not merely a disabled input). To change them you edit the Contact.
//  2. GST is per line, from each product's tax profile, and CGST+SGST vs IGST
//     is decided from the client's place of supply rather than typed. The
//     Studio applies one flat CGST/SGST pair to every row, which silently
//     mis-taxes every inter-state sale.
//  3. Save Draft parks an unfinished quotation without it counting as sent.
//     Only Save & Issue moves the pipeline record to Quotation Sent.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileSpreadsheet, Package, Trash2, Boxes, Search, AlertTriangle, ExternalLink, Check, PenLine, CalendarClock, Plus } from 'lucide-react';
import {
  FollowupCaptureFields,
  followupCapturePayload,
  EMPTY_FOLLOWUP_CAPTURE,
  type FollowupCapture,
} from '@/features/activity/components/FollowupCaptureFields';

import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { CURRENCY_OPTIONS } from '@/shared/constants/currencies';
import { convertInrToForeign, convertForeignToInr } from '@/shared/utils/currencyConversion';

// GST pattern (client, 2026-10-02) — chosen on the quotation, carried to the
// proforma / sales order / invoice. Server rule: src/shared/sales/gst-pattern.js.
type GstPattern = 'domestic' | 'sez_lut' | 'export';
const SEZ_LUT_RATE = 0.1;
const GST_PATTERN_OPTIONS: { value: GstPattern; label: string }[] = [
  { value: 'domestic', label: 'Domestic GST' },
  { value: 'sez_lut', label: 'SEZ / LUT – 0.1% GST' },
  { value: 'export', label: 'Export – Without GST (0%)' },
];

export const DEFAULT_FALLBACK_TERMS = [
  { _id: 'term-payment-advance', title: 'Payment: 100% Advance against PI', body: '1. Payment: 100% Advance against Proforma Invoice.' },
  { _id: 'term-payment-credit', title: 'Payment: 30 Days Credit against PDC', body: '1. Payment: 30 days credit from invoice date against post-dated cheque.' },
  { _id: 'term-payment-dispatch', title: 'Payment: 50% Advance, 50% before dispatch', body: '1. Payment: 50% Advance with Purchase Order and 50% balance prior to dispatch.' },
  { _id: 'term-freight-extra', title: 'Freight: Extra at actuals', body: '2. Freight: Extra at actuals payable by buyer / consignee.' },
  { _id: 'term-freight-paid', title: 'Freight: Paid & included in price (F.O.R)', body: '2. Freight: Included in unit prices (F.O.R Destination).' },
  { _id: 'term-delivery-weeks', title: 'Delivery: 2-3 Weeks from PO date', body: '3. Delivery: Within 2 to 3 weeks upon receipt of confirmed Purchase Order and advance.' },
  { _id: 'term-delivery-exstock', title: 'Delivery: Ready / Ex-Stock', body: '3. Delivery: Ready stock / Ex-stock subject to prior sale.' },
  { _id: 'term-validity-30', title: 'Validity: 30 Days from quotation date', body: '4. Validity: This quotation is valid for 30 days from the date of issue.' },
  { _id: 'term-validity-15', title: 'Validity: 15 Days from quotation date', body: '4. Validity: This quotation is valid for 15 days from the date of issue.' },
  { _id: 'term-warranty-1yr', title: 'Warranty: 12 Months standard warranty', body: '5. Warranty: 12 months manufacturer standard warranty against manufacturing defects.' },
  { _id: 'term-taxes-gst', title: 'Taxes: GST 18% Extra as applicable', body: '6. Taxes: GST extra as applicable at current statutory rates at the time of invoicing.' },
];
import { Spinner } from '@/shared/components/ui/Spinner';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR, formatMoney } from '@/shared/utils/currency';
import { packSummary, ratePerBaseUnit, totalBaseQuantityLabel, piecesPerPack, baseUnitDivisor } from '@/shared/utils/ratePerBaseUnit';
import { useContacts, useContactMutations } from '@/features/contacts/hooks/useContacts';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useHasPermission } from '@/shared/permissions/useHasPermission';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import { usePipelineRecord } from '@/features/pipeline/hooks/usePipeline';

import {
  useBillToPreview, useCatalogProducts, useQuotationTerms, useQuotationTotals,
  emptyLine, lineFromProduct, lineFromHierarchyItem, type QuotationLine, type CatalogProduct,
} from '../hooks/useQuotationForm';
import { BillToPanel } from '../components/BillToPanel';
import { EditContactButton } from '../components/EditContactButton';
import { InvoicePoPanel } from '../components/InvoicePoPanel';
import { ProductHierarchySelector } from '@/features/catalog/components/ProductHierarchySelector';
import { UnitPackCell } from '@/features/catalog/components/UnitPackCell';
import type { ProductHierarchyItem } from '@/features/catalog/hooks/useCatalogApi';

/** yyyy-mm-dd for an <input type="date">. */
const isoDate = (d: Date) => d.toISOString().slice(0, 10);

const VALIDITY_KEY = 'rbm.quoteValidityDays';
const VALIDITY_CHOICES = [7, 15, 30, 45];
const readValidityDays = () => {
  try {
    const n = Number(localStorage.getItem(VALIDITY_KEY));
    return VALIDITY_CHOICES.includes(n) ? n : 15;
  } catch { return 15; }
};
/** yyyy-mm-dd + n days, computed on the calendar date (no timezone drift). */
const addDaysIso = (iso: string, days: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  t.setUTCDate(t.getUTCDate() + days);
  return t.toISOString().slice(0, 10);
};

/** Bill To as edited on a tax invoice. */
interface InvoiceBillTo { name: string; gstin: string; street: string; city: string; state: string; zipCode: string; country: string }

export function QuotationFormPage({ editDocumentType = 'quotation', accountsInvoice = false }: { editDocumentType?: 'quotation' | 'proforma' | 'direct_sale' | 'sales_order'; accountsInvoice?: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams<{ id: string }>();
  const dealId = searchParams.get('dealId');
  const cloneKey = searchParams.get('clone');
  const cloneNoLines = searchParams.get('cloneNoLines') === '1';
  // Clone a single quotation/proforma/invoice directly from its own detail
  // page (SalesDocDetailPage's Clone button) — the source document's id, no
  // pipeline record required. Distinct from `cloneKey`, which clones one of
  // several documents surfaced by a pipeline record's Customer History panel
  // and therefore needs `dealId` to know which record to scan.
  const cloneFrom = searchParams.get('cloneFrom');
  const isCloning = !!cloneKey || !!cloneFrom;
  const documentType = editId ? editDocumentType : isCloning && searchParams.get('type') === 'invoice' ? 'direct_sale'
    : isCloning && searchParams.get('type') === 'proforma' ? 'proforma'
    // "New Proforma" (/proforma/new) opens this same form as a proforma.
    : editDocumentType === 'proforma' ? 'proforma' : 'quotation';
  const documentLabel = documentType === 'direct_sale' ? 'Invoice' : documentType === 'proforma' ? 'Proforma Invoice' : documentType === 'sales_order' ? 'Sales Order' : 'Quotation';
  const listRoute = documentType === 'direct_sale' ? ROUTES.invoices : documentType === 'proforma' ? ROUTES.proforma : documentType === 'sales_order' ? ROUTES.salesOperations : ROUTES.quotations;
  const isEdit = !!editId;
  // A new document for an incomplete lead is refused by the server
  // (LEAD_INCOMPLETE) — say so before anything is typed (client, 2026-10-28).
  const { data: leadForGaps } = useQuery({
    queryKey: ['pipeline', 'lead-gaps', dealId],
    enabled: Boolean(dealId) && !isEdit,
    queryFn: async () => (await api.get<{ leadGaps?: string[]; business_name?: string; title?: string }>(`/api/pipeline/${dealId}`)).data,
    staleTime: 0,
  });
  const leadGapsForQuote = !isEdit ? (leadForGaps?.leadGaps ?? []) : [];

  // ── Client ────────────────────────────────────────────────────────────────
  const [contactId, setContactId] = useState<string>('');
  const [acknowledgedSupply, setAcknowledgedSupply] = useState(false);

  // Shipping address — the bill-to block above resolves automatically from
  // the CRM contact, but there was never anywhere on this form to record a
  // DIFFERENT delivery address, so the printed document always fell back to
  // "same as billing" (the renderer's own default when shipTo is blank).
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shipCompanyName, setShipCompanyName] = useState('');
  const [shipStreet, setShipStreet] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');
  const [shipPhone, setShipPhone] = useState('');
  const [shipEmail, setShipEmail] = useState('');
  // Pipeline owns this quotation's delivery-address snapshot. Keep it
  // separately from the editable inputs so a later Bill-to lookup cannot
  // reset the form to the billing address.
  const [pipelineShipTo, setPipelineShipTo] = useState<Record<string, string> | null>(null);
  const [shippingCharge, setShippingCharge] = useState<number | string>('');
  const [roundOffInput, setRoundOffInput] = useState<string>('');
  const [roundOffManual, setRoundOffManual] = useState(false);

  // Mandatory follow-up fields for quotations (Ticket 110 & 111)
  // All three start BLANK (client: every user fills the follow-up by hand, as
  // a practice). Pre-filled values were being saved untouched.
  // Item 44 — one capture set, the same one the Follow-up panel uses, so the
  // follow-up logged inside a quotation is not a thinner record than one added
  // to it later.
  const [followup, setFollowup] = useState<FollowupCapture>(EMPTY_FOLLOWUP_CAPTURE);
  // Mandatory when a quotation is RAISED. Editing one later does not need a new
  // follow-up — one was logged the first time (the server checks the same thing
  // from the record); a follow-up entered while editing is still saved.
  const followupRequired = documentType === 'quotation' && !isEdit;

  // Foreign-currency billing — SalesOrderModel already has currencyConversion/
  // conversionRate/fromCurrency/toCurrency/baseGrandTotal (db-sales.js) and
  // POST/PUT /api/sales-ops/orders already reads them, but nothing on this
  // form ever set them, so an export quotation had no way to record what
  // currency the client was actually quoted in or the rate used to book it.
  const [currencyConversion, setCurrencyConversion] = useState(false);
  // INR, but no GST — an export billed in rupees (Nepal / Bhutan).
  const [gstPattern, setGstPattern] = useState<GstPattern>('domestic');
  const [fromCurrency, setFromCurrency] = useState('INR');
  const [conversionRate, setConversionRate] = useState<number | string>('');
  // FX rate currently reflected in line item prices (e.g. 90). When the user
  // edits the rate input (e.g. 90 -> 89), this lets the form detect the change and
  // offers instant re-scaling or keeping line prices.
  const [appliedFxRate, setAppliedFxRate] = useState<number | null>(null);
  const [invoiceFxNote, setInvoiceFxNote] = useState<{ tone: 'info' | 'warning'; text: string } | null>(null);
  // INR per unit of the currency a tax invoice is billed in; 0 = billed in INR.
  const [invoiceFxRate, setInvoiceFxRate] = useState(0);
  // The billing currency was changed on this invoice edit (client, 2026-10-07).
  const [currencyTouched, setCurrencyTouched] = useState(false);
  // Bill To edited on the invoice itself (client, 2026-10-07); sent only when changed.
  const [invBillTo, setInvBillTo] = useState<InvoiceBillTo | null>(null);
  const [invBillToLoaded, setInvBillToLoaded] = useState('');
  const [seller, setSeller] = useState<{ code: string; name: string }>({ code: '', name: '' });
  // The linked contact's details as they are now, and which empty Bill To
  // fields were filled from it.
  const [contactBillTo, setContactBillTo] = useState<InvoiceBillTo | null>(null);
  const [contactFilled, setContactFilled] = useState<string[]>([]);

  // Live rate from /api/forex (market feed, central-bank fallback). Called
  // whenever the billing currency is set or changed, so the rate always
  // belongs to the currency on screen — switching USD → EUR used to keep the
  // USD rate, and the auto-fill for foreign clients called a URL that does not
  // exist, so no rate was ever filled in and the document saved at 1.
  const [fxLoading, setFxLoading] = useState(false);
  const fxRequest = useRef(0);
  const loadLiveRate = useCallback(async (code: string, { quiet = false } = {}): Promise<number | null> => {
    if (!code || code === 'INR') return null;
    const ticket = ++fxRequest.current;
    setFxLoading(true);
    try {
      const { data } = await api.get<{ rate?: number }>(`/api/forex/rate/${code}/INR`);
      if (ticket !== fxRequest.current) return null; // a newer currency was chosen meanwhile
      if (Number(data?.rate) > 0) {
        const rate = Number(Number(data.rate).toFixed(4));
        setConversionRate(rate);
        setAppliedFxRate(rate);
        return rate;
      }
      if (!quiet) toast.error('Could not fetch a live rate. Enter it manually.');
    } catch {
      if (ticket === fxRequest.current && !quiet) toast.error('Could not fetch a live rate. Enter it manually.');
    } finally {
      if (ticket === fxRequest.current) setFxLoading(false);
    }
    return null;
  }, []);
  const fxFetching = fxLoading;
  // Without a rate the server used to fall back to 1, so a USD 1,000 document
  // was valued at ₹1,000. Saving waits for a real rate instead.
  const needsFxRate = !accountsInvoice && currencyConversion && fromCurrency !== 'INR' && !(Number(conversionRate) > 0);

  // Same formatter the quotation/proforma lists use (formatDocTotal → formatMoney),
  // so the figure a coordinator types and the figure the list shows can never
  // disagree on grouping. INR docs render ₹12,34,567 via formatINR.
  const fmtMoney = (amount: number) => formatMoney(amount, currencyConversion ? fromCurrency : 'INR');

  const { createContact } = useContactMutations();
  // The panel does its own lookup; the page reads the same cached result for
  // the tax split and the issue-time validation.
  const { data: billToPreview } = useBillToPreview(contactId);

  // Address, mobile, email and GSTIN follow the contact on a quotation or
  // proforma that can still be edited (client, 2026-10-24). The name is left
  // alone: "Edit name" on the document is a deliberate choice.
  const contactSyncable = isEdit && !accountsInvoice && !!contactId && ['quotation', 'proforma'].includes(documentType);
  const syncFromContact = useCallback(async (quiet = false) => {
    if (!editId) return;
    try {
      const { data } = await api.post<{ order: { customerGstin?: string; customerPhone?: string; customerEmail?: string; billingAddress?: Record<string, string> } }>(
        `/api/sales-ops/orders/${editId}/sync-contact-address`, { keepName: true },
      );
      const o = data.order || {};
      const b = o.billingAddress || {};
      setSavedClient((prev) => (prev ? {
        ...prev,
        gstin: o.customerGstin ?? prev.gstin,
        phone: o.customerPhone ?? prev.phone,
        email: o.customerEmail ?? prev.email,
        address: [b.street, b.city, b.state, b.zipCode].filter(Boolean).join(', ') || prev.address,
        billingAddress: { ...(prev.billingAddress || {}), country: b.country || prev.billingAddress?.country },
      } : prev));
      setContactSyncNote('Address, mobile and email updated from the contact.');
      if (!quiet) toast.success('Contact saved — this document now has the same address and mobile.');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      if (!quiet) toast.error(e?.response?.data?.error || 'Could not update the document from the contact.');
    }
  }, [editId]);

  // Invoice Edit: Locked Client Name & Contact Fetching
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactPickerSearch, setContactPickerSearch] = useState('');
  const debouncedContactSearch = useDebounce(contactPickerSearch.trim(), 300);
  const { data: contactsData, isLoading: searchingContacts } = useContacts({
    search: debouncedContactSearch || undefined,
    compact: true,
    totalOnly: false,
    limit: 25,
  });

  const handleSelectInvoiceContact = async (c: any) => {
    try {
      const res = await api.get(`/api/sales-ops/orders/bill-to/${c._id}`);
      const b = res.data?.billTo;
      const addr = b?.billingAddress || c.billingAddress || {};
      const newBillTo: InvoiceBillTo = {
        name: b?.customerName || c.name || c.companyName || '',
        gstin: String(b?.customerGstin || c.gstin || c.gstNumber || '').toUpperCase(),
        street: addr.street || (typeof c.address === 'string' ? c.address : '') || '',
        city: addr.city || c.city || '',
        state: addr.state || c.state || '',
        zipCode: addr.zipCode || c.pincode || '',
        country: addr.country || c.country || 'India',
      };
      setInvBillTo(newBillTo);
      setContactBillTo(newBillTo);
      setContactId(c._id);
      setShowContactPicker(false);
      toast.success(`Client details fetched from Contact: ${newBillTo.name}`);
    } catch {
      toast.error('Failed to load contact details.');
    }
  };

  // ── Document ──────────────────────────────────────────────────────────────
  const [date, setDate] = useState(isoDate(new Date()));
  // Validity is the rep's call. Quick picks set it from the document date, and
  // the last pick is remembered on this browser for the next new document, so
  // nobody pages the calendar back every time. A hand-picked date wins.
  const [validityDays, setValidityDays] = useState<number | null>(() => (isEdit ? null : readValidityDays()));
  const [validUntil, setValidUntil] = useState(() => (isEdit ? '' : addDaysIso(isoDate(new Date()), readValidityDays())));
  const pickValidity = (days: number) => {
    setValidityDays(days);
    setValidUntil(addDaysIso(date, days));
    try { localStorage.setItem(VALIDITY_KEY, String(days)); } catch { /* storage blocked: still works for this form */ }
  };
  // No Payment Terms control on the form any more (client: "we only need
  // Terms & Notes") — payment conditions go in the terms text. The state is
  // kept only so editing an older document re-saves the value it already had;
  // a new document stores none, rather than a hidden "100% Advance".
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  // Commercial terms are a first-class snapshot, separate from internal
  // notes/dispatch instructions. This is what the terms-library dropdown
  // edits and what is carried to proforma, invoice and challan.
  const [termsText, setTermsText] = useState('');
  // Starts empty, not with one blank row — same as the pipeline record's
  // ProductLinesEditor, which shows an explicit "No Products Added Yet"
  // empty state instead. A blank row that silently does nothing until you
  // start typing into it reads as broken; an empty state with a search bar
  // and an "Add Custom Line" button reads as an empty list waiting for input.
  const [lines, setLines] = useState<QuotationLine[]>([]);

  // ── Changing the document currency converts what is already typed ────────
  // (client, 2026-10-06: "rate typed in INR, then switched to USD — it did not
  // convert"). Rates, fixed discounts and freight are in the DOCUMENT's
  // currency, so they move from the old currency to the new one through INR:
  // new = old × (old → ₹) ÷ (new → ₹). A manual round-off is reset so it is
  // worked out again. If the live rate cannot be fetched, the conversion waits
  // for the user to enter the rate and press "Convert rates".
  const [pendingFx, setPendingFx] = useState<{ fromCode: string; fromRate: number } | null>(null);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const hasTypedAmounts = () => lines.some((l) => Number(l.unitPrice) > 0) || Number(shippingCharge) > 0;
  const switchCurrency = (code: string) => {
    const nextCode = !code ? 'INR' : code;
    if (accountsInvoice) setCurrencyTouched(true);
    const prevCode = currencyConversion ? fromCurrency : 'INR';
    if (nextCode === prevCode) return;
    const basis = pendingFx ?? { fromCode: prevCode, fromRate: prevCode === 'INR' ? 1 : Number(conversionRate) };
    const canConvert = hasTypedAmounts() && basis.fromRate > 0;
    if (nextCode === 'INR') {
      setCurrencyConversion(false);
      setFromCurrency('INR');
      setConversionRate('');
      setPendingFx(null);
      setAppliedFxRate(null);
      // Canonical Rule: Always revert to stored baseInrPrice. Never compound!
      setLines((ls) => ls.map((l) => ({
        ...l,
        unitPrice: l.baseInrPrice ?? (basis.fromRate > 0 ? convertForeignToInr(l.unitPrice, basis.fromRate) : (Number(l.unitPrice) || 0)),
      })));
      return;
    }
    // A tax invoice keeps the GST pattern it was raised with.
    if (!accountsInvoice) setGstPattern('export');
    setCurrencyConversion(true);
    setFromCurrency(nextCode);
    setConversionRate('');
    setPendingFx(canConvert ? basis : null);
    void loadLiveRate(nextCode).then((rate) => {
      if (rate && canConvert) {
        setLines((ls) => ls.map((l) => {
          // Always convert from canonical baseInrPrice: foreignPrice = baseInrPrice / rate
          const baseInr = l.baseInrPrice ?? (basis.fromRate > 0 ? convertForeignToInr(l.unitPrice, basis.fromRate) : (Number(l.unitPrice) || 0));
          return {
            ...l,
            baseInrPrice: baseInr,
            unitPrice: convertInrToForeign(baseInr, rate),
          };
        }));
        setPendingFx(null);
        setAppliedFxRate(rate);
      } else if (rate) {
        setAppliedFxRate(rate);
      }
    });
  };

  const applyRateChange = useCallback((newRate: number) => {
    if (!(newRate > 0)) return;
    const oldRate = appliedFxRate;

    // Convert lines deterministically from baseInrPrice (Foreign Price = INR Base Price / newRate)
    setLines((ls) => ls.map((l) => {
      const baseInr = l.baseInrPrice ?? (oldRate && oldRate > 0 ? convertForeignToInr(l.unitPrice, oldRate) : (Number(l.unitPrice) || 0));
      const newUnitPrice = convertInrToForeign(baseInr, newRate);
      const fxFactor = oldRate && oldRate > 0 ? oldRate / newRate : 1 / newRate;
      return {
        ...l,
        baseInrPrice: baseInr,
        unitPrice: newUnitPrice,
        discountAmount: l.discountType === 'fixed' ? round2((Number(l.discountAmount) || 0) * fxFactor) : l.discountAmount,
      };
    }));

    if (oldRate && oldRate > 0) {
      const factor = oldRate / newRate;
      setShippingCharge((prev) => (String(prev).trim() === '' ? prev : round2((Number(prev) || 0) * factor)));
    } else if (hasTypedAmounts()) {
      setShippingCharge((prev) => (String(prev).trim() === '' ? prev : convertInrToForeign(Number(prev) || 0, newRate)));
    }

    setPendingFx(null);
    setAppliedFxRate(newRate);
    toast.success(`Prices updated in ${fromCurrency} at 1 ${fromCurrency} = ₹${newRate}.`);
  }, [appliedFxRate, fromCurrency, hasTypedAmounts]);

  const debouncedFxRate = useDebounce(conversionRate, 650);
  useEffect(() => {
    if (!currencyConversion || fromCurrency === 'INR') return;
    const val = Number(debouncedFxRate);
    if (val > 0) {
      if (appliedFxRate === null || Math.abs(val - appliedFxRate) > 0.0001 || pendingFx) {
        applyRateChange(val);
      }
    }
  }, [debouncedFxRate, currencyConversion, fromCurrency, appliedFxRate, pendingFx, applyRateChange]);

  // convertPendingNow / rescaleLinesToNewRate / convertInrLinesToForeign were
  // removed here: three unreferenced shims that each just called
  // applyRateChange(), left over from the pre-canonical conversion approach.
  // The live path is switchCurrency() + applyRateChange(), both of which work
  // from the stored baseInrPrice and therefore cannot compound a conversion.
  const [lineDiscountType, setLineDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [saving, setSaving] = useState(false);

  // ── Minimum-price floor ──────────────────────────────────────────────────
  const canOverrideFloor = useHasPermission('pricing.override_floor');
  const [floorBlock, setFloorBlock] = useState<{
    message: string;
    violations: { lineNo: number; reason: string }[];
    pendingIssue: boolean;
  } | null>(null);
  const [floorReason, setFloorReason] = useState('');

  // ── Editing a finalized Invoice/Tax Invoice ───────────────────────────────
  // Only an Admin/Superadmin's edit here applies immediately; anyone else's
  // is queued for their approval instead (src/api-sales/orders.js,
  // src/api-accounts/sales-invoices.js — both key off the exact same
  // role check, isSystemAdmin). The server never reads this reason for an
  // Admin/Superadmin, so the box is hidden for them entirely — showing a
  // "reason required" prompt to the one role it was never enforced against
  // read as this system asking a Superadmin for permission it already holds
  // unconditionally, which is exactly the confusion the client flagged.
  const currentUser = useUser();
  // "Credit this sale to" (client, 2026-10-28): the Sales Coordinator raises
  // documents for reps who are away, and sells herself too — she chooses.
  // Only she and admins see it; the server enforces the same (orders.js).
  const canChooseCredit = ['sales_coordinator', 'admin', 'superadmin', 'department_admin'].includes(String(currentUser?.role || ''));
  const [creditTo, setCreditTo] = useState('');
  const { data: creditUsers = [] } = useUserOptions(canChooseCredit);
  const creditOptions = useMemo(() => creditUsers
    .filter((u) => u.role !== 'superadmin' && u.role !== 'driver'
      && (String(u.department || '').toLowerCase() === 'sales' || String(u.username || '').toLowerCase().startsWith('bdm')))
    .map((u) => ({ value: u.id, label: u.id === currentUser?.id ? `${u.display_name} (me)` : u.display_name }))
    .sort((a, b) => a.label.localeCompare(b.label)), [creditUsers, currentUser?.id]);
  const isSystemAdminUser = ['admin', 'superadmin'].includes(currentUser?.role || '');
  const [changeReason, setChangeReason] = useState('');
  const [pendingApprovalNotice, setPendingApprovalNotice] = useState('');
  // Set synchronously, unlike `saving`: two quick clicks both ran save() before
  // the re-render disabled the button, and each sent its own approval request.
  const savingRef = useRef(false);

  const { data: termPresets = [] } = useQuotationTerms();
  const effectiveTerms = useMemo(() => {
    return (termPresets && termPresets.length > 0) ? termPresets : DEFAULT_FALLBACK_TERMS;
  }, [termPresets]);

  // ── Loading an existing quotation for editing ─────────────────────────────
  //
  // Edits now keep the same document id/number even after issue/finalization.
  // That is what keeps Dispatch, Accounts and Super Admin looking at one live
  // copy instead of a duplicate sibling.
  const [loadingDoc, setLoadingDoc] = useState(isEdit || isCloning);
  const [loadError, setLoadError] = useState('');
  const [savedClient, setSavedClient] = useState<{ name: string; gstin: string; address: string; intraState: boolean; billingAddress?: { country?: string }; phone?: string; email?: string } | null>(null);
  const [contactSyncNote, setContactSyncNote] = useState('');
  const contactSyncDone = useRef('');
  const [docNumber, setDocNumber] = useState('');
  const [editingStatus, setEditingStatus] = useState('');

  // Opening an editable quotation whose contact has changed since it was
  // saved brings the contact's address / mobile / email / GSTIN across once.
  useEffect(() => {
    if (!contactSyncable || !savedClient || !billToPreview?.billTo) return;
    if (!['draft', 'quotation', 'proforma'].includes(String(editingStatus || ''))) return;
    const key = `${editId}:${contactId}`;
    if (contactSyncDone.current === key) return;
    contactSyncDone.current = key;
    const b = billToPreview.billTo;
    const a = b.billingAddress || {};
    const norm = (x?: string) => String(x || '').toLowerCase().replace(/[\s,]+/g, ' ').trim();
    const changed = (live?: string, saved?: string) => !!norm(live) && norm(live) !== norm(saved);
    if (changed([a.street, a.city, a.state, a.zipCode].filter(Boolean).join(', '), savedClient.address)
      || changed(b.customerPhone, savedClient.phone) || changed(b.customerEmail, savedClient.email) || changed(b.customerGstin, savedClient.gstin)) {
      void syncFromContact(true);
    }
  }, [contactSyncable, savedClient, billToPreview, editingStatus, editId, contactId, syncFromContact]);
  // Rename the client from here (client, 2026-10-06): updates this document
  // AND the contact (and so the client's other open quotations) in one go.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const renameClient = async () => {
    if (!editId) return;
    setRenameBusy(true);
    try {
      const { data } = await api.post<{ customerName: string }>(`/api/sales-ops/orders/${editId}/client-name`, { name: renameValue.trim() });
      setSavedClient((prev) => (prev ? { ...prev, name: data.customerName } : prev));
      setRenaming(false);
      queryClient.invalidateQueries({ queryKey: ['sales', 'bill-to'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Client name updated on this quotation and in the contact.');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || 'Could not rename the client.');
    } finally {
      setRenameBusy(false);
    }
  };
  // The pipeline record an edited invoice came from — where its customer PO lives.
  const [linkedRecordId, setLinkedRecordId] = useState('');
  // The document the PO is kept on when there is no pipeline record: the
  // quotation a tax invoice came from, or the proforma / order being edited.
  const [poDocId, setPoDocId] = useState('');
  useEffect(() => {
    if (!editId && !(cloneKey && dealId) && !cloneFrom) return;
    let cancelled = false;
    // Three sources of "load an existing document's fields": editing it in
    // place, cloning one of several documents a pipeline record's Customer
    // History panel found (needs `dealId` to know which record), or cloning
    // a single document directly by its own id (SalesDocDetailPage's Clone
    // button). All three land on the same shape below — the clone paths just
    // skip every isEdit-gated setter (docNumber, status, date, validUntil),
    // which is exactly "keep everything except the number and the date".
    const url = editId
      ? `/api/${accountsInvoice ? 'accounts/sales-invoices' : 'sales-ops/orders'}/${editId}`
      : cloneFrom
        ? `/api/sales-ops/orders/${cloneFrom}`
        : `/api/pipeline/${dealId}/customer-history?clone=${encodeURIComponent(cloneKey!)}`;
    api.get(url)
      .then(({ data }) => {
        if (cancelled) return;
        const d = data?.order || data;
        if (!d || (isEdit && !accountsInvoice && d.documentType !== documentType)) { setLoadError('This document could not be loaded.'); return; }
        const customer = typeof d.customerId === 'object' && d.customerId ? d.customerId : {};
        const sourceLines = accountsInvoice
      ? d.lineItems || []
      : cloneNoLines
        ? []
        : d.lines || [];
        const address = d.billingAddress || customer.billingAddress || customer.address || {};
        if (isEdit) setSavedClient({
          name: d.customerName || customer.name || '',
          // An invoice's accounts customer keeps it as gstNumber.
          gstin: d.customerGstin || customer.gstNumber || customer.gstin || '',
          address: typeof address === 'string' ? address : [address.street, address.city, address.state, address.zipCode].filter(Boolean).join(', '),
          intraState: !sourceLines.some((l: { igstRate?: number }) => Number(l.igstRate) > 0),
          billingAddress: typeof address === 'string' ? undefined : { country: address.country },
          phone: d.customerPhone || customer.phone || '',
          email: d.customerEmail || customer.email || '',
        });
        if (accountsInvoice && isEdit) {
          const a = (typeof address === 'object' && address ? address : {}) as Record<string, string>;
          const bt: InvoiceBillTo = {
            name: d.billToName || d.customerName || customer.name || '',
            gstin: d.billToName ? d.billToGstin || '' : d.customerGstin || customer.gstNumber || customer.gstin || '',
            street: a.street || (typeof address === 'string' ? address : ''),
            city: a.city || '', state: a.state || '', zipCode: a.zipCode || '', country: a.country || 'India',
          };
          setInvBillTo(bt);
          setInvBillToLoaded(JSON.stringify(bt));
          setSeller({ code: String(d.sellerStateCode || ''), name: String(d.sellerStateName || '') });
          // The contact as it is NOW (client, 2026-10-08): a correction made in
          // Contacts reaches this Bill To automatically. Only a Bill To already
          // changed by hand on this invoice is kept — the contact's details are
          // then one click away instead, and only its empty fields are filled.
          const manualBillTo = Boolean(d.billToEditedAt);
          const idOf = (v: unknown) => (v && typeof v === 'object' ? String((v as { _id?: unknown })._id || '') : v ? String(v) : '');
          const srcId = idOf(d.crmSalesDocId || d.sourceDocumentId);
          const findContactId = async (): Promise<string> => {
            if (d.crmContactId) return idOf(d.crmContactId);
            if (srcId) {
              const { data: src } = await api.get(`/api/sales-ops/orders/${srcId}`);
              const o = src?.order || src;
              // Ship To fields the invoice left empty: the quotation's, as the
              // printed invoice already shows them — never sent back blank.
              const sa = (o?.shippingAddress || {}) as Record<string, string>;
              if (!cancelled && (sa.street || sa.city)) {
                setShipCompanyName((v) => v || sa.companyName || sa.name || '');
                setShipStreet((v) => v || sa.street || '');
                setShipCity((v) => v || sa.city || '');
                setShipState((v) => v || sa.state || '');
                setShipZip((v) => v || sa.zipCode || '');
              }
              const cid = idOf(o?.crmContactId);
              if (cid) return cid;
            }
            // An invoice raised straight from a pipeline record: its contact.
            const dealId = idOf(d.crmDealId);
            if (dealId) {
              const { data: rec } = await api.get(`/api/pipeline/${dealId}`);
              const r = rec?.record || rec;
              return idOf(r?.contactId || r?.contact_id);
            }
            return '';
          };
          findContactId()
            .then((cid) => (cid ? api.get(`/api/sales-ops/orders/bill-to/${cid}`) : null))
            .then((r) => {
              const b = r?.data?.billTo;
              if (cancelled || !b) return;
              const ca = b.billingAddress || {};
              const fromContact: InvoiceBillTo = {
                name: b.customerName || '', gstin: String(b.customerGstin || '').toUpperCase(),
                street: ca.street || '', city: ca.city || '', state: ca.state || '', zipCode: ca.zipCode || '', country: ca.country || 'India',
              };
              setContactBillTo(fromContact);
              const changed = (Object.keys(bt) as (keyof InvoiceBillTo)[]).filter((k) => String(fromContact[k] || '').trim()
                && fromContact[k] !== bt[k] && (!manualBillTo || !String(bt[k] || '').trim()));
              if (!changed.length) return;
              setContactFilled(changed);
              // A field already retyped on this screen stays as typed.
              setInvBillTo((cur) => {
                if (!cur) return cur;
                const next = { ...cur };
                for (const k of changed) if (cur[k] === bt[k]) next[k] = fromContact[k];
                return next;
              });
            })
            .catch(() => { /* the contact is a convenience here, not required */ });
        }
        setDocNumber(isEdit ? d.invoiceNumber || d.orderNumber || '' : '');
        if (isEdit && ['cancelled', 'expired', 'superseded', 'voided', 'reversed'].includes(d.status)) {
          setLoadError('This document is closed and cannot be edited.'); return;
        }
        if (isEdit) setEditingStatus(d.status || '');
        setContactId(d.crmContactId ? String(d.crmContactId) : '');
        if (isEdit) setLinkedRecordId(d.crmDealId ? String(d.crmDealId) : '');
        if (isEdit) setPoDocId(accountsInvoice ? String(d.crmSalesDocId || d.sourceDocumentId || '') : String(editId || ''));
        if (isEdit && d.date) setDate(String(d.date).slice(0, 10));
        const rawDate = accountsInvoice ? d.dueDate : (d.validUntil || d.dueDate);
        if (isEdit) setValidUntil(rawDate ? String(rawDate).slice(0, 10) : '');
        setPaymentTerms(d.paymentTerms || '');
        if (isEdit && d.originatingRepId) setCreditTo(String((d.originatingRepId as { _id?: string })?._id || d.originatingRepId));
        setNotes(d.notes || '');
        setTermsText(Array.isArray(d.terms)
          ? d.terms.map((term: unknown) => typeof term === 'string' ? term : (term as { body?: string; text?: string; title?: string })?.body || (term as { text?: string })?.text || (term as { title?: string })?.title || '').filter(Boolean).join('\n')
          : (d.terms || ''));
        // Restore shipping address from the source document so that cloning
        // (or editing) preserves a distinct delivery address rather than
        // silently resetting it to "same as billing".
        const shipAddr = d.shippingAddress && typeof d.shippingAddress === 'object' ? d.shippingAddress as Record<string, string> : null;
        if (shipAddr && (shipAddr.companyName || shipAddr.name || shipAddr.street || shipAddr.city || shipAddr.state || shipAddr.zipCode || shipAddr.phone || shipAddr.email)) {
          setSameAsBilling(false);
          setShipCompanyName(shipAddr.companyName || shipAddr.name || '');
          setShipStreet(shipAddr.street || '');
          setShipCity(shipAddr.city || '');
          setShipState(shipAddr.state || '');
          setShipZip(shipAddr.zipCode || '');
          setShipPhone(shipAddr.phone || '');
          setShipEmail(shipAddr.email || '');
        } else {
          setSameAsBilling(true);
          setShipCompanyName(''); setShipStreet(''); setShipCity(''); setShipState(''); setShipZip('');
          setShipPhone(''); setShipEmail('');
        }
        // A tax invoice stores its freight as freightCharges; sales documents as shippingCharge.
        const loadedFreight = accountsInvoice ? d.freightCharges : d.shippingCharge;
        if (loadedFreight) setShippingCharge(Number(loadedFreight));
        setLineDiscountType(d.lines?.some((line: QuotationLine) => line.discountType === 'fixed') ? 'fixed' : 'percent');
        if (d.roundOff !== undefined && d.roundOff !== null) {
          setRoundOffInput(String(d.roundOff));
          setRoundOffManual(false);
        }
        setCurrencyConversion(Boolean(d.currencyConversion));
        setGstPattern(d.gstPattern === 'sez_lut' || d.gstPattern === 'export' ? d.gstPattern : d.exportNoGst ? 'export' : 'domestic');
        setFromCurrency(d.fromCurrency && d.fromCurrency !== 'INR' ? d.fromCurrency : 'USD');
        const loadedFx = d.currencyConversion && d.conversionRate ? Number(d.conversionRate) : '';
        setConversionRate(loadedFx);
        if (typeof loadedFx === 'number' && loadedFx > 0) {
          setAppliedFxRate(loadedFx);
        }
        // A tax invoice raised from a USD/EUR quotation is billed in that
        // currency (client, 2026-10-07): the form shows and edits its prices
        // in it, exactly as quoted. It is booked in INR at the rate it was
        // raised at — converted back on save (see handleSave).
        const lineFx = accountsInvoice && d.foreignCurrency && Number(d.foreignRate) > 0 && Number(d.foreignRate) !== 1
          ? Number(d.foreignRate) : 1;
        const fromInr = (n: unknown, dp = 2) => Math.round((Number(n) || 0) / lineFx * 10 ** dp) / 10 ** dp;
        setInvoiceFxRate(lineFx !== 1 ? lineFx : 0);
        if (accountsInvoice) {
          if (lineFx !== 1) {
            setCurrencyConversion(true);
            setFromCurrency(String(d.foreignCurrency));
            setConversionRate(lineFx);
            setShippingCharge(fromInr(loadedFreight));
            setInvoiceFxNote({ tone: 'info', text: `Billed in ${d.foreignCurrency}, as quoted. Booked in INR at 1 ${d.foreignCurrency} = ₹${lineFx} for GST and accounts.` });
          } else if (d.crmSalesDocId || d.sourceDocumentId) {
            // Raised before that conversion existed: the amounts were copied
            // from a foreign-currency quotation unchanged, so they ARE in that
            // currency. Show them as such, and say plainly that they need checking.
            api.get(`/api/sales-ops/orders/${String(d.crmSalesDocId || d.sourceDocumentId)}`)
              .then(({ data: src }) => {
                const o = src?.order || src;
                if (cancelled || !o?.currencyConversion || !o.fromCurrency || o.fromCurrency === 'INR') return;
                setCurrencyConversion(true);
                setFromCurrency(o.fromCurrency);
                setConversionRate(Number(o.conversionRate) > 0 ? Number(o.conversionRate) : '');
                setInvoiceFxNote({ tone: 'warning', text: `This invoice was created before foreign-currency conversion was fixed: its amounts are in ${o.fromCurrency}, not INR. Check with Accounts before finalising it.` });
              })
              .catch(() => { /* opens in INR, as before */ });
          }
        }
        setLines(sourceLines.map((l: Record<string, unknown>) => ({
          ...emptyLine(),
          productId: typeof l.productId === 'object' && l.productId ? String((l.productId as { _id: string })._id) : (l.productId as string) || null,
          incomeAccountId: (l.incomeAccountId as string) || null,
          description: (l.description as string) || '',
          notes: (l.notes as string) || '',
          hsnSac: (l.hsnSac as string) || '',
          quantity: Number(l.quantity) || 1,
          unitPrice: accountsInvoice ? fromInr(l.rate, 4) : Number(l.unitPrice) || 0,
          unit: (l.unit as string) || 'LTR',
          packSize: (l.packSize as string) || '',
          qtyPerPack: Number(l.qtyPerPack) || 0,
          totalPackSize: (l.totalPackSize as string) || '',
          volumeLiters: Number(l.volumeLiters) || 0,
          discountType: accountsInvoice || l.discountType === 'fixed' ? 'fixed' : 'percent',
          discountPercent: !accountsInvoice && l.discountType === 'percent' ? Number(l.discount) || 0 : 0,
          discountAmount: accountsInvoice ? fromInr(l.discount) : l.discountType === 'fixed' ? Number(l.discount) || 0 : 0,
          gstRate: (Number(l.cgstRate) || 0) + (Number(l.sgstRate) || 0) + (Number(l.igstRate) || 0),
        })));
      })
      .catch(() => { if (!cancelled) setLoadError('This document could not be loaded.'); })
      .finally(() => { if (!cancelled) setLoadingDoc(false); });
    return () => { cancelled = true; };
  }, [editId, cloneKey, cloneFrom, dealId, accountsInvoice, documentType]);

  // A clone is a NEW document: it keeps the source's currency but is priced
  // at today's rate, not the rate the source was issued at. Runs once, after
  // the source has loaded. An edit keeps its saved rate (Fetch Live Rate
  // refreshes it on request).
  const clonedRateFetched = useRef(false);
  useEffect(() => {
    if (!isCloning || loadingDoc || clonedRateFetched.current) return;
    clonedRateFetched.current = true;
    if (currencyConversion && fromCurrency !== 'INR') void loadLiveRate(fromCurrency, { quiet: true });
  }, [isCloning, loadingDoc, currencyConversion, fromCurrency, loadLiveRate]);

  // ── Prefill from the pipeline record ──────────────────────────────────────
  // The record supplies the CONTACT, priced product lines and this enquiry's
  // own Ship-to address.  A pipeline record is not a legacy Deal: loading
  // `/api/deals/:id` here silently lost its structured shippingAddress and
  // made the form fall back to the Contact's old/default (RBM) address.
  const [prefilling, setPrefilling] = useState(!!dealId && !isEdit && !isCloning);
  const [recordName, setRecordName] = useState('');
  // React Query is also the path the Pipeline detail page uses. Keeping the
  // record here means a quotation always observes the latest transaction
  // Ship-to, even when this form mounted before its one-off prefill request
  // completed.
  const { data: pipelineRecord } = usePipelineRecord(dealId);
  useEffect(() => {
    // An existing document already carries its own client and lines; the
    // record's would overwrite edits made since it was created.
    if (!dealId || isEdit || isCloning) return;
    let cancelled = false;
    api.get(`/api/pipeline/${dealId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const deal = data?.record || data?.deal || data;
        if (!deal) return;
        setRecordName(deal.business_name || deal.name || '');
        if (deal.contactId || deal.contact_id) setContactId(String(deal.contactId || deal.contact_id));
        const hasStoredShipTo = deal.shippingAddress && (deal.shippingAddress.companyName || deal.shippingAddress.name || deal.shippingAddress.street || deal.shippingAddress.city || deal.shippingAddress.state || deal.shippingAddress.zipCode);
        const dealShip = hasStoredShipTo ? deal.shippingAddress : deal.billingAddress;
        if (dealShip && (dealShip.companyName || dealShip.name || dealShip.street || dealShip.city || dealShip.state || dealShip.zipCode)) {
          setPipelineShipTo(dealShip);
          setSameAsBilling(false);
          setShipCompanyName(dealShip.companyName || dealShip.name || '');
          setShipStreet(dealShip.street || '');
          setShipCity(dealShip.city || '');
          setShipState(dealShip.state || '');
          setShipZip(dealShip.zipCode || '');
        }
        if (deal.shippingCharge) setShippingCharge(Number(deal.shippingCharge));
        setLineDiscountType(deal.lines?.some((line: QuotationLine) => line.discountType === 'fixed') ? 'fixed' : 'percent');
        if (deal.currency && deal.currency !== 'INR') {
          setCurrencyConversion(true);
          setFromCurrency(deal.currency);
        }
        if (Array.isArray(deal.lines) && deal.lines.length > 0) {
          setLines(deal.lines.map((l: Record<string, unknown>) => ({
            ...emptyLine(),
            productId: (l.productId as string) || null,
            description: (l.description as string) || (l.productName as string) || '',
            notes: (l.notes as string) || '',
            hsnSac: (l.hsnSac as string) || '',
            quantity: Number(l.quantity) || 1,
            unitPrice: Number(l.unitPrice) || 0,
            unit: (l.unit as string) || 'LTR',
            packSize: (l.packSize as string) || '',
            qtyPerPack: Number(l.qtyPerPack) || 0,
            totalPackSize: (l.totalPackSize as string) || '',
            volumeLiters: Number(l.volumeLiters) || 0,
            discountType: l.discountType === 'fixed' ? 'fixed' : 'percent',
            discountPercent: l.discountType === 'percent' ? Number(l.discount) || 0 : 0,
            discountAmount: l.discountType === 'fixed' ? Number(l.discount) || 0 : 0,
          })));
        }
      })
      .catch(() => { /* a prefill failure still leaves a usable blank form */ })
      .finally(() => { if (!cancelled) setPrefilling(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  useEffect(() => {
    if (!pipelineRecord || isEdit || isCloning) return;
    const ship = pipelineRecord.shippingAddress as Record<string, string> | undefined;
    if (!ship || !(ship.companyName || ship.name || ship.street || ship.city || ship.state || ship.zipCode)) return;
    setPipelineShipTo(ship);
  }, [pipelineRecord, isEdit, isCloning]);

  // Apply the Pipeline snapshot after the asynchronous contact/Bill-to
  // hydration. This makes the Pipeline's distinct Ship-to authoritative for
  // the visible form as well as the eventual saved quotation.
  useEffect(() => {
    if (!pipelineShipTo) return;
    setSameAsBilling(false);
    setShipCompanyName(pipelineShipTo.companyName || pipelineShipTo.name || '');
    setShipStreet(pipelineShipTo.street || '');
    setShipCity(pipelineShipTo.city || '');
    setShipState(pipelineShipTo.state || '');
    setShipZip(pipelineShipTo.zipCode || '');
    setShipPhone(pipelineShipTo.phone || '');
    setShipEmail(pipelineShipTo.email || '');
  }, [pipelineShipTo]);

  // Auto-detect foreign client or distinct shipping address from contact
  useEffect(() => {
    if (!billToPreview?.billTo) return;
    const b = billToPreview.billTo;
    const s = b.shippingAddress;
    if (s && (s.companyName || s.name || s.street || s.city || s.state || s.zipCode) && !dealId && !isEdit && !isCloning) {
      setShipCompanyName(s.companyName || s.name || '');
      setShipStreet(s.street || '');
      setShipCity(s.city || '');
      setShipState(s.state || '');
      setShipZip(s.zipCode || '');
      setSameAsBilling(false);
    }
    // A pipeline record's own currency wins: the rep chose it for this
    // enquiry, and the customer's address alone would reset it to INR.
    const recordCurrency = dealId && pipelineRecord?.currency && pipelineRecord.currency !== 'INR' ? pipelineRecord.currency : '';
    const isForeign = Boolean(recordCurrency || b.isInternational ||
      (b.billingAddress?.country && b.billingAddress.country.toLowerCase() !== 'india') ||
      (b.currency && b.currency !== 'INR'));
    if (!isEdit && !isCloning) {
      // A newly chosen client starts from its own default currency and pattern.
      setGstPattern(isForeign ? 'export' : 'domestic');
      if (isForeign) {
        setCurrencyConversion(true);
        const curr = recordCurrency || ((b.currency && b.currency !== 'INR') ? b.currency : 'USD');
        setFromCurrency(curr);
        setConversionRate('');
        void loadLiveRate(curr, { quiet: true });
      } else {
        setCurrencyConversion(false);
        setFromCurrency('INR');
        setConversionRate('');
      }
    }
  }, [billToPreview, isEdit, isCloning, dealId, pipelineRecord?.currency, loadLiveRate]);

  // ── Totals ────────────────────────────────────────────────────────────────
  // Default to intra-state before a contact is chosen so the preview reads
  // sensibly; nothing can be saved in that state anyway.
  const billToEdited = accountsInvoice && !!invBillTo && JSON.stringify(invBillTo) !== invBillToLoaded;
  // An edited Bill To decides CGST + SGST or IGST the way the server will on
  // save (GSTIN state first, then the state name; unknown → IGST).
  const editedIntra = (() => {
    if (!billToEdited || !invBillTo || !seller.code) return null;
    const g = invBillTo.gstin.trim().toUpperCase();
    if (/^[0-9]{2}[A-Z0-9]{13}$/.test(g)) return g.slice(0, 2) === seller.code;
    const st = invBillTo.state.trim().toLowerCase();
    return !!st && st === seller.name.trim().toLowerCase();
  })();
  const intraState = editedIntra ?? savedClient?.intraState ?? billToPreview?.intraState ?? true;
  const clientCountry = String(billToPreview?.billTo?.billingAddress?.country || savedClient?.billingAddress?.country || '').trim().toLowerCase();
  // A foreign currency or a buyer abroad is always an export.
  const forcedExport = !accountsInvoice && Boolean((clientCountry && clientCountry !== 'india') || (currencyConversion && fromCurrency !== 'INR'));
  const effectivePattern: GstPattern = forcedExport ? 'export' : gstPattern;
  const isInternational = effectivePattern === 'export';
  const gstOverride = effectivePattern === 'sez_lut' ? SEZ_LUT_RATE : null;
  const totals = useQuotationTotals(
    lines,
    intraState,
    Number(shippingCharge) || 0,
    isInternational,
    // Every currency rounds to the whole unit (₹ or $), unless typed by hand.
    roundOffManual && roundOffInput.trim() !== '' ? Number(roundOffInput) : undefined,
    gstOverride,
  );

  // Fetched only on demand (the button below), not on every keystroke — a
  // rate typed manually (a negotiated/locked rate) should never be silently
  // overwritten by a background refetch.

  // ── Validation ────────────────────────────────────────────────────────────
  const filledLines = useMemo(() => lines.filter((l) => l.description.trim()), [lines]);
  const needsSupplyAck = !isEdit && !!billToPreview?.assumedPlaceOfSupply && !acknowledgedSupply;
  const blockingIssues = useMemo(() => {
    const out: string[] = [];
    if (!contactId && !savedClient) out.push('Choose the client this document is for.');
    if (filledLines.length === 0) out.push('Add at least one line item.');
    if (!isEdit && billToPreview?.missingAddress) out.push('The client has no address in Contacts.');
    if (needsSupplyAck) out.push('Confirm the place of supply.');
    if (followupRequired) {
      if (!followup.followupDate) out.push('Select a follow-up date.');
      if (!followup.followupCategory) out.push('Select a follow-up category.');
      if (!followup.followupNote.trim()) out.push('Add a follow-up note.');
      if (followup.followupCategory === 'custom' && !followup.customCategory.trim()) out.push('Specify the custom follow-up category.');
    }
    if (needsFxRate) out.push(`Enter the ${fromCurrency} → INR exchange rate (or press Fetch Live Rate).`);
    return out;
  }, [contactId, savedClient, isEdit, filledLines.length, billToPreview, needsSupplyAck, documentType, followup, needsFxRate, fromCurrency]);

  // A missing GSTIN is a WARNING, not a blocker. Many of RBM's clients are
  // unregistered and are quoted perfectly legitimately without one — the
  // document simply prints a blank GSTIN row. It stays visible so a rep who
  // expected one on file notices it is not there.
  const warnings = useMemo(() => {
    const out: string[] = [];
    if (contactId && billToPreview?.missingGstin) {
      out.push('This client has no GSTIN on file — the document will show a blank GSTIN.');
    }
    return out;
  }, [contactId, billToPreview]);

  // Save Draft only needs a client and a line — the point of a draft is to park
  // something incomplete. Issuing is what demands a complete bill-to block.
  // For Quotation, Follow-up is mandatory (Ticket 110).
  const canSaveDraft = (!!contactId || !!savedClient) && filledLines.length > 0 && !saving && !pendingApprovalNotice && !needsFxRate && (!followupRequired || (Boolean(followup.followupDate) && Boolean(followup.followupCategory) && Boolean(followup.followupNote.trim()) && (followup.followupCategory !== 'custom' || Boolean(followup.customCategory.trim()))));
  const canIssue = blockingIssues.length === 0 && !saving;

  // ── Line editing ──────────────────────────────────────────────────────────
  const setLine = (key: string, patch: Partial<QuotationLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  // No special-case floor at one line any more — removing the last line just
  // empties the list back to the empty state, same as ProductLinesEditor.
  const removeLine = (key: string) =>
    setLines((ls) => ls.filter((l) => l.key !== key));

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async (asDraft: boolean, overrideReason?: string) => {
    if (followupRequired) {
      if (!followup.followupDate) {
        toast.error('Follow-up date is mandatory for saving Quotation.');
        return;
      }
      if (!followup.followupCategory) {
        toast.error('Follow-up category is mandatory for saving Quotation.');
        return;
      }
      if (!followup.followupNote.trim()) {
        toast.error('Follow-up note is mandatory for saving Quotation.');
        return;
      }
      if (followup.followupCategory === 'custom' && !followup.customCategory.trim()) {
        toast.error('Please specify the custom follow-up category.');
        return;
      }
    }
    // A change already waiting for approval must be decided before another is
    // sent (client, 2026-10-23) — the server refuses it too.
    if (savingRef.current || pendingApprovalNotice) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const rawBilling = (accountsInvoice && invBillTo
        ? {
            street: invBillTo.street || '',
            city: invBillTo.city || '',
            state: invBillTo.state || '',
            zipCode: invBillTo.zipCode || '',
            country: invBillTo.country || 'India',
          }
        : (savedClient?.billingAddress || billToPreview?.billTo?.billingAddress || {
            street: savedClient?.address || '',
            city: '',
            state: '',
            zipCode: '',
            country: 'India',
          })) as Record<string, any>;

      const effectiveBilling = {
        street: String(rawBilling?.street || ''),
        city: String(rawBilling?.city || ''),
        state: String(rawBilling?.state || ''),
        zipCode: String(rawBilling?.zipCode || ''),
        country: String(rawBilling?.country || 'India'),
      };

      const effectiveCustomerName = accountsInvoice
        ? (invBillTo?.name || savedClient?.name || '')
        : (savedClient?.name || billToPreview?.billTo?.customerName || recordName || '');

      const previewBilling = billToPreview?.billTo?.billingAddress;
      const resolvedShippingAddress = sameAsBilling || !(shipCompanyName.trim() || shipStreet.trim() || shipCity.trim() || shipState.trim() || shipZip.trim() || shipPhone.trim() || shipEmail.trim())
        ? {
            companyName: effectiveCustomerName,
            name: effectiveCustomerName,
            street: effectiveBilling.street,
            city: effectiveBilling.city,
            state: effectiveBilling.state,
            zipCode: effectiveBilling.zipCode,
            country: effectiveBilling.country,
            phone: accountsInvoice ? ((rawBilling?.phone as string) || '') : (previewBilling?.phone || ''),
            email: accountsInvoice ? ((rawBilling?.email as string) || '') : (previewBilling?.email || ''),
            gstin: accountsInvoice ? (invBillTo?.gstin || '') : (billToPreview?.billTo?.customerGstin || savedClient?.gstin || ''),
          }
        : {
            companyName: shipCompanyName.trim(),
            name: shipCompanyName.trim(),
            street: shipStreet.trim(),
            city: shipCity.trim(),
            state: shipState.trim(),
            zipCode: shipZip.trim(),
            country: 'India',
            phone: shipPhone.trim() || undefined,
            email: shipEmail.trim() || undefined,
          };

      const payload = {
        documentType,
        // Only the create route reads this; an edit changes status through
        // /issue instead, so that publishing is one explicit action.
        ...(isEdit ? {} : { saveAsDraft: asDraft }),
        ...(overrideReason ? { floorOverrideReason: overrideReason } : {}),
        crmContactId: contactId,
        crmDealId: dealId || undefined,
        ...(canChooseCredit && creditTo ? { originatingRepId: creditTo } : {}),
        customerName: effectiveCustomerName,
        date,
        validUntil: validUntil || undefined,
        // A quotation's validity is not an invoice payment deadline.  For a
        // direct sale or an edited Tax Invoice this field is the Due Date the
        // user selected in the form and must be sent independently.
        dueDate: (documentType === 'direct_sale' || accountsInvoice) ? (validUntil || undefined) : undefined,
        paymentTerms,
        notes,
        billingAddress: effectiveBilling,
        shippingAddress: resolvedShippingAddress,
        shippingCharge: Number(shippingCharge) || 0,
        lines: filledLines.map((l, i) => {
          const t = totals.lines[lines.indexOf(l)] ?? totals.lines[i];
          return {
            productId: l.productId || null,
            incomeAccountId: l.incomeAccountId || null,
            description: l.description,
            // This mapping is an explicit whitelist, so a field absent here is
            // silently discarded however carefully it was typed — which is
            // exactly what happened to the per-line note.
            notes: l.notes || '',
            hsnSac: l.hsnSac,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            unit: l.unit,
            packSize: l.packSize,
            qtyPerPack: l.qtyPerPack,
            totalPackSize: l.totalPackSize,
            volumeLiters: l.volumeLiters,
            discount: l.discountType === 'fixed' ? l.discountAmount : l.discountPercent,
            discountType: l.discountType,
            lineTotal: t?.taxable ?? 0,
            cgstRate: t?.cgstRate ?? 0,
            sgstRate: t?.sgstRate ?? 0,
            igstRate: t?.igstRate ?? 0,
          };
        }),
        subTotal: totals.subTotal,
        taxTotal: totals.taxTotal,
        roundOff: roundOffManual && roundOffInput.trim() !== '' ? Number(roundOffInput) : totals.roundOff,
        manualRoundOff: roundOffManual,
        terms: termsText.split(/\r?\n/).map((term) => term.trim()).filter(Boolean),
        grandTotal: totals.grandTotal,
        currencyConversion,
        fromCurrency: currencyConversion ? fromCurrency : 'INR',
        gstPattern: effectivePattern,
        exportNoGst: effectivePattern === 'export',
        toCurrency: 'INR',
        conversionRate: currencyConversion ? Number(conversionRate) || 1 : 1,
        ...(documentType === 'quotation' && (followupRequired || followup.followupNote.trim()) ? {
          followup: followupCapturePayload(followup),
        } : {}),
      };

      const wasAlreadyFinal = isEdit && editingStatus !== 'draft';

      let saved;
      let status = 200;
      if (isEdit && accountsInvoice) {
        // Billed in USD/EUR: the amounts typed are in that currency; the
        // invoice is booked in INR at its own rate (same paisa rounding the
        // invoice was generated with, generate-invoice.js toInr).
        // The rate the typed amounts are in: the invoice's own, or the one
        // picked on this edit. A legacy invoice left untouched keeps as it was.
        const fxNow = currencyConversion && fromCurrency !== 'INR' && (invoiceFxRate > 0 || currencyTouched)
          ? Number(conversionRate) || 0 : 0;
        if (currencyTouched && currencyConversion && fromCurrency !== 'INR' && !(fxNow > 0)) {
          toast.error(`Enter the ${fromCurrency} → INR rate (or press Fetch Live Rate).`);
          return;
        }
        const inr = (n: unknown) => (fxNow > 0
          ? Math.round((Number(n) || 0) * fxNow * 100) / 100
          : Number(n) || 0);
        const resp = await api.put(`/api/accounts/sales-invoices/${editId}`, {
          date, dueDate: validUntil || undefined, paymentTerms, notes, terms: termsText,
          roundOff: roundOffManual && roundOffInput.trim() !== '' ? Number(roundOffInput) : totals.roundOff,
          manualRoundOff: roundOffManual,
          shippingAddress: (payload as { shippingAddress?: unknown }).shippingAddress,
          billingAddress: (payload as { billingAddress?: unknown }).billingAddress,
          freightCharges: fxNow > 0 ? inr((payload as { shippingCharge?: unknown }).shippingCharge) : (payload as { shippingCharge?: unknown }).shippingCharge,
          ...(fxNow > 0 ? { foreignGrandTotal: totals.grandTotal } : {}),
          ...(currencyTouched ? { currency: { code: fxNow > 0 ? fromCurrency : 'INR', rate: fxNow } } : {}),
          ...(billToEdited && invBillTo ? { billTo: invBillTo } : {}),
          lineItems: payload.lines.map((line) => ({
            ...line, rate: inr(line.unitPrice),
            discount: inr(line.discountType === 'percent'
              ? line.quantity * line.unitPrice * line.discount / 100
              : line.discount),
          })),
          ...(wasAlreadyFinal ? { reason: changeReason } : {}),
        });
        saved = resp.data; status = resp.status;
      } else if (isEdit) {
        // PUT keeps the document, its number and its place in the chain. The
        // status is NOT sent. Drafts can still be issued explicitly below;
        // issued/final documents are just updated in place.
        const resp = await api.put(`/api/sales-ops/orders/${editId}`, {
          ...payload,
          ...(wasAlreadyFinal ? { reason: changeReason } : {}),
        });
        saved = resp.data; status = resp.status;
        if (!asDraft && editingStatus === 'draft') {
          ({ data: saved } = await api.post(`/api/sales-ops/orders/${editId}/issue`,
            overrideReason ? { floorOverrideReason: overrideReason } : {}));
        }
      } else {
        ({ data: saved } = await api.post('/api/sales-ops/orders', payload));
      }
      // Every list and detail this save can change — refreshed including the
      // ones not on screen, so the list this form returns to is already current.
      await Promise.all([
        ['sales-ops'], ['quotations'], ['proforma'], ['sales-doc'], ['invoices'], ['invoices-direct'], ['accounts'],
        ['invoice-requests'], ['invoice-edit-requests'], ['pipeline'], ['deals'], ['leads'], ['payments'],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      // …and anything else on screen right now.
      await queryClient.invalidateQueries();
      setFloorBlock(null);
      setFloorReason('');

      // 202 = an Admin/Superadmin has not applied this yet — nothing on the
      // document changed. Say so plainly rather than claiming "updated", and
      // stay on the page so the reason box (and whatever was typed) is still
      // visible if it needs adjusting before resubmitting.
      if (status === 202 && saved?.pendingApproval) {
        setPendingApprovalNotice(saved.message || 'Submitted for Admin/Superadmin approval. Nothing has been changed yet.');
        toast.success('Submitted for Admin/Superadmin approval.');
        return;
      }

      toast.success(
        isEdit && (asDraft || editingStatus !== 'draft')
          ? `${documentLabel} ${saved?.invoiceNumber || saved?.orderNumber || docNumber || ''} updated.`
          : asDraft
          ? 'Saved as a draft. It has not been issued yet.'
          : `${documentLabel} ${saved?.invoiceNumber || saved?.orderNumber || docNumber || ''} issued.`,
      );
      // `replace: true` — otherwise this form stays as a browser-back target
      // behind the just-saved document, with no trace on screen that it was
      // ever created. Pressing Back would land right back on a blank/reset
      // form for the same client, inviting a second document to be raised by
      // mistake — same fix as NewPipelineRecordPage's post-create redirect.
      navigate(dealId ? `/pipeline/${dealId}` : listRoute, { replace: true });
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string; code?: string; violations?: { lineNo: number; reason: string }[] } };
      };
      const data = e.response?.data;
      if (data?.code === 'EDIT_REQUEST_PENDING') {
        setPendingApprovalNotice(data.error || 'A change to this document is already waiting for approval.');
        toast.error(data.error || 'A change is already waiting for approval.');
      } else if (data?.code === 'REASON_REQUIRED') {
        toast.error(data.error || 'A reason is required to request a change to a finalized invoice.');
      } else if (data?.code === 'BELOW_MIN_PRICE') {
        // A line is priced below its product's floor. Show which, and — for a
        // user who can override — a reason box that re-submits.
        setFloorBlock({
          message: data.error || 'One or more lines are below the minimum selling price.',
          violations: data.violations || [],
          pendingIssue: !asDraft,
        });
      } else {
        toast.error(data?.error || 'The document could not be saved.');
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // ── Create a Contact from the pipeline record ─────────────────────────────
  // Offered rather than done automatically: silently creating one is how the
  // duplicate client profiles happened. The picker is shown first so an
  // existing account can be linked instead.
  const createFromRecord = async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/deals/${dealId}`);
      const deal = data?.deal || data;
      const created = await createContact.mutateAsync({
        name: deal.business_name || deal.name || 'New client',
        company: deal.business_name || '',
        email: deal.email || '',
        phone: deal.phone || '',
        notes: 'Created from a pipeline record while raising a quotation.',
      } as never);
      const newId = (created as { id?: string })?.id;
      if (!newId) throw new Error('no id');
      // Link it back, so the next quotation from this record finds it.
      await api.patch(`/api/pipeline/${dealId}`, { contactId: newId });
      setContactId(newId);
      toast.success('Contact created and linked to this record.');
    } catch {
      toast.error('The contact could not be created.');
    }
  };

  if (prefilling || loadingDoc) {
    return (
      <div aria-busy="true" aria-live="polite" className="flex w-full flex-1 flex-col gap-6 p-4 md:p-6 lg:p-8">
        <Skeleton className="h-10 w-72 rounded-lg" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
          <Skeleton className="h-64 rounded-card" />
        </div>
        <Skeleton className="h-96 rounded-card" />
        <span className="sr-only">Loading the document…</span>
      </div>
    );
  }

  if (leadGapsForQuote.length > 0 && dealId) {
    const LABEL: Record<string, string> = { product: 'the product', contact: 'the person you spoke to', followup: 'a next follow-up' };
    return (
      <div className="flex w-full flex-1 items-center justify-center p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-md rounded-card border border-border bg-card p-6 text-center shadow-xs">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning-text" aria-hidden="true" />
          <h2 className="mt-3 text-headline-sm font-semibold text-on-surface">Complete this lead first</h2>
          <p className="mt-2 text-body-md text-on-surface-variant">
            {leadForGaps?.business_name || leadForGaps?.title || 'This lead'} still needs {leadGapsForQuote.map((g) => LABEL[g] || g).join(', ')}.
            Nothing can be raised for it until that is filled in.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => navigate(`/pipeline/${dealId}`)}>Open the lead</Button>
          </div>
        </div>
      </div>
    );
  }

  // An issued quotation is not editable. Say why, and point at the action that
  // IS available rather than leaving a dead end.
  if (loadError) {
    return (
      <div className="flex w-full flex-1 items-center justify-center p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-md rounded-card border border-border bg-card p-6 text-center shadow-xs">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning-text" aria-hidden="true" />
          <h2 className="mt-3 text-headline-sm font-semibold text-on-surface">This document can&apos;t be edited</h2>
          <p className="mt-2 text-body-md text-on-surface-variant">{loadError}</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Button variant="outline" onClick={() => navigate(listRoute)}>Back to documents</Button>
            {editId && (
              <Button onClick={() => navigate(`${listRoute}/${editId}`)}>Open the document</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-6 p-4 md:p-6 lg:p-8">
      {/* ── Header / sticky action bar ─────────────────────────────────────── */}
      <header className="sticky top-0 z-20 -mx-4 -mt-4 flex flex-wrap items-center justify-between gap-4 border-b border-border bg-card px-4 py-4 shadow-xs md:-mx-6 md:-mt-6 md:px-6 lg:-mx-8 lg:-mt-8 lg:px-8">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(dealId ? `/pipeline/${dealId}` : listRoute)}
            aria-label="Back"
            className="h-10 w-10 rounded-lg border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-xs font-semibold uppercase tracking-wider text-primary">Sales</span>
              <span className="text-body-xs text-on-surface-variant">/</span>
              <span className="text-body-xs text-on-surface-variant">
                {isEdit ? `Edit ${docNumber || documentLabel}` : `New ${documentLabel}`}
              </span>
            </div>
            <h1 className="mt-0.5 flex items-center gap-2.5 text-display-sm font-semibold text-on-surface">
              <FileSpreadsheet className="h-6 w-6 text-primary" />
              {isEdit ? `Edit ${docNumber || documentLabel}` : `New ${documentLabel}`}
            </h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* The visual sheet editor, for anyone who wants it — but only when
              CREATING. The Studio always POSTs a NEW document and cannot save
              into an existing one, so from an edit screen it opened a blank
              sheet (with its own sample line) that had nothing to do with the
              quotation being edited. Use Download to see the real document. */}
          {!isEdit && !isCloning && (
          <Link
            to={`/documents/create?type=quotation${dealId ? `&dealId=${encodeURIComponent(dealId)}` : ''}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-2 text-body-xs font-semibold text-on-surface transition hover:bg-surface-container-low"
          >
            <PenLine className="h-3.5 w-3.5" />
            Open in Studio
            <ExternalLink className="h-3 w-3 opacity-60" />
          </Link>
          )}
          <Button
            variant={documentType === 'direct_sale' ? 'primary' : 'outline'}
            onClick={() => save(true)}
            disabled={!canSaveDraft}
            className="px-5 font-bold"
          >
            {isEdit ? (saving ? 'Saving Changes…' : 'Save Changes') : 'Save Draft'}
          </Button>
          {documentType !== 'direct_sale' && (!isEdit || editingStatus === 'draft') && (
            <Button onClick={() => save(false)} disabled={!canIssue} className="px-5 font-bold">
              {saving ? 'Saving…' : 'Save & Issue'}
            </Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {accountsInvoice && isEdit && invBillTo ? (
          <div className="flex flex-col gap-3 rounded-card border border-border bg-card p-5 shadow-xs">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-body-sm font-semibold text-on-surface">Company / Client Name</label>
                <span className="text-data-xs font-semibold text-success-text bg-success/10 px-2 py-0.5 rounded-md">
                  Linked to CRM Contact
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-lg border border-outline/70 bg-muted px-3 py-2 text-body-sm font-semibold text-on-surface select-none shadow-2xs">
                  {invBillTo.name || <span className="text-on-surface-subtle italic">No contact name</span>}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowContactPicker(!showContactPicker)}
                  className="shrink-0 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/5"
                >
                  <Search className="w-3.5 h-3.5" />
                  <span>{showContactPicker ? 'Close' : 'Select Contact'}</span>
                </Button>
              </div>
              <p className="text-data-xs text-on-surface-variant">
                Client name is fetched directly from Contacts to prevent typing mismatches.
              </p>
            </div>

            {showContactPicker && (
              <div className="rounded-xl border border-primary/20 bg-muted p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-on-surface-subtle shrink-0" />
                  <input
                    type="text"
                    placeholder="Search CRM Contacts by company or name..."
                    value={contactPickerSearch}
                    onChange={(e) => setContactPickerSearch(e.target.value)}
                    className="flex-1 bg-white rounded-lg border border-outline px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-border rounded-lg border border-border bg-white">
                  {searchingContacts ? (
                    <div className="p-3 text-center text-xs text-on-surface-variant flex items-center justify-center gap-2">
                      <Spinner className="w-3.5 h-3.5" /> Searching contacts...
                    </div>
                  ) : (contactsData?.contacts || []).length === 0 ? (
                    <div className="p-3 text-center text-xs text-on-surface-variant">
                      No contacts found matching &ldquo;{contactPickerSearch}&rdquo;.
                    </div>
                  ) : (
                    (contactsData?.contacts || []).map((c: any) => (
                      <button
                        key={c._id}
                        type="button"
                        onClick={() => handleSelectInvoiceContact(c)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-primary/5 transition flex items-center justify-between"
                      >
                        <div>
                          <p className="font-semibold text-on-surface">{c.companyName || c.name}</p>
                          <p className="text-data-xs text-on-surface-variant">{[c.city, c.state].filter(Boolean).join(', ')} {c.gstin ? `· GSTIN: ${c.gstin}` : ''}</p>
                        </div>
                        <span className="text-data-xs text-primary font-semibold">Select</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            <label className="flex flex-col gap-1 text-body-sm font-medium">
              Address / Street
              <textarea
                className="min-h-[64px] rounded-lg border border-outline px-3 py-2 text-body-sm font-normal"
                value={invBillTo.street}
                onChange={(e) => setInvBillTo({ ...invBillTo, street: e.target.value })}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Input label="City" value={invBillTo.city} onChange={(e) => setInvBillTo({ ...invBillTo, city: e.target.value })} />
              <Input label="State" value={invBillTo.state} onChange={(e) => setInvBillTo({ ...invBillTo, state: e.target.value })} />
              <Input label="Pincode" value={invBillTo.zipCode} onChange={(e) => setInvBillTo({ ...invBillTo, zipCode: e.target.value })} />
              <Input label="Country" value={invBillTo.country} onChange={(e) => setInvBillTo({ ...invBillTo, country: e.target.value })} />
            </div>
            <Input label="GSTIN" value={invBillTo.gstin} maxLength={15} placeholder="Leave blank if unregistered / export"
              onChange={(e) => setInvBillTo({ ...invBillTo, gstin: e.target.value.toUpperCase() })} />
            {contactFilled.length > 0 && (
              <p className="text-xs text-on-surface-variant">
                Updated from the contact: {contactFilled.map((k) => ({ name: 'name', gstin: 'GSTIN', street: 'address', city: 'city', state: 'state', zipCode: 'pincode', country: 'country' } as Record<string, string>)[k] || k).join(', ')}.
              </p>
            )}
            {contactBillTo && JSON.stringify(contactBillTo) !== JSON.stringify(invBillTo) && (
              <button type="button" className="self-start text-body-xs font-semibold text-primary hover:underline"
                onClick={() => setInvBillTo({ ...contactBillTo })}>
                Use the contact's current details ({contactBillTo.name}{contactBillTo.gstin ? `, ${contactBillTo.gstin}` : ''})
              </button>
            )}
            <p className="text-primary">{isInternational ? 'No GST (export)' : intraState ? 'CGST + SGST' : 'IGST'}</p>
            {billToEdited && (
              <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm font-medium text-warning-text">
                Bill To changed — printed on this invoice{isInternational ? '' : ', and the GST type follows the state'}.
                {editingStatus && editingStatus !== 'draft' ? ' This invoice is finalized — the change goes to an Admin/Superadmin for approval.' : ''}
              </p>
            )}
          </div>
        ) : isEdit && savedClient ? (
          <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-5 shadow-xs">
            <h2 className="border-b border-border pb-3 text-headline-sm font-semibold text-on-surface">Bill to</h2>
            <p className="text-body-xs text-on-surface-variant">Fixed for this {documentLabel.toLowerCase()}</p>
            {renaming ? (
              <div className="flex flex-col gap-2">
                <input
                  className="rounded-lg border border-outline px-3 py-2 text-body-sm"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Client name on the quotation"
                  autoFocus
                />
                <p className="text-body-xs text-on-surface-variant">Saved in the contact too — and on this client's other open quotations / proformas.</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={renameClient} disabled={renameBusy || renameValue.trim().length < 2}>
                    {renameBusy ? 'Saving…' : 'Update quotation & contact'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRenaming(false)} disabled={renameBusy}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <strong>{savedClient.name || 'Customer'}</strong>
                {contactId && !accountsInvoice && ['quotation', 'proforma'].includes(documentType) && ['draft', 'quotation', 'proforma'].includes(String(editingStatus || '')) && (
                  <button type="button" className="shrink-0 text-body-xs font-semibold text-primary hover:underline"
                    onClick={() => { setRenameValue(savedClient.name || ''); setRenaming(true); }}>
                    Edit name
                  </button>
                )}
              </div>
            )}
            {!renaming && contactId && !accountsInvoice && ['quotation', 'proforma'].includes(documentType) && billToPreview?.billTo?.customerName && billToPreview.billTo.customerName !== savedClient.name && (
              <p className="text-body-xs text-warning-text">
                The contact now says <b>{billToPreview.billTo.customerName}</b>.{' '}
                <button type="button" className="font-semibold underline" onClick={() => { setRenameValue(billToPreview.billTo.customerName); setRenaming(true); }}>
                  Use it
                </button>
              </p>
            )}
            {/* A document made before the client's details were filled in has a
                blank copy; show the contact as it is now (saving fills it in). */}
            <p>{savedClient.address || [billToPreview?.billTo?.billingAddress?.street, billToPreview?.billTo?.billingAddress?.city, billToPreview?.billTo?.billingAddress?.state, billToPreview?.billTo?.billingAddress?.zipCode].filter(Boolean).join(', ')}</p>
            <p>GSTIN: {savedClient.gstin || billToPreview?.billTo?.customerGstin || '—'}</p>
            {(savedClient.phone || savedClient.email || billToPreview?.billTo?.customerPhone) && (
              <p className="text-on-surface-variant">
                {[savedClient.phone || billToPreview?.billTo?.customerPhone, savedClient.email || billToPreview?.billTo?.customerEmail].filter(Boolean).join(' · ')}
              </p>
            )}
            {contactSyncable && ['draft', 'quotation', 'proforma'].includes(String(editingStatus || '')) && (
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <EditContactButton contactId={contactId} onSaved={() => syncFromContact(false)} />
                {contactSyncNote && <span className="text-body-xs text-success-text">{contactSyncNote}</span>}
              </div>
            )}
            <p className="text-primary">{isInternational ? 'No GST (export)' : intraState ? 'CGST + SGST' : 'IGST'}</p>
          </div>
        ) : <BillToPanel
          documentLabel={documentLabel}
          contactId={contactId}
          onSelect={setContactId}
          onClear={() => setContactId('')}
          onCreateFromRecord={dealId && !isEdit && !cloneKey ? createFromRecord : undefined}
          creatingContact={createContact.isPending}
          locked={isEdit}
          acknowledgedSupply={acknowledgedSupply}
          onAcknowledgeSupply={setAcknowledgedSupply}
        />}

        {/* ── Shipping address ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 rounded-card border border-border bg-card p-5 shadow-xs">
          <h2 className="border-b border-border pb-3 text-headline-sm font-semibold text-on-surface">Ship to</h2>
          <label className="flex items-center gap-2 text-body-sm text-on-surface-variant">
            <input
              type="checkbox"
              checked={sameAsBilling}
              onChange={(e) => {
                const next = e.target.checked;
                setSameAsBilling(next);
                if (next) setPipelineShipTo(null);
                if (!next) {
                  const b = billToPreview?.billTo?.billingAddress;
                  if (!shipCompanyName && !shipStreet && !shipCity) {
                    setShipCompanyName(billToPreview?.billTo?.customerName || savedClient?.name || '');
                    if (b) {
                      setShipStreet(b.street || '');
                      setShipCity(b.city || '');
                      setShipState(b.state || '');
                      setShipZip(b.zipCode || '');
                    }
                  }
                }
              }}
            />
            Same as billing address
          </label>
          {sameAsBilling ? (
            <p className="text-body-sm text-on-surface-variant">
              {savedClient?.address || [
                billToPreview?.billTo?.billingAddress?.street,
                [billToPreview?.billTo?.billingAddress?.city, billToPreview?.billTo?.billingAddress?.zipCode].filter(Boolean).join(' '),
                billToPreview?.billTo?.billingAddress?.state,
              ].filter(Boolean).join(', ') || 'Same address as billing (shown once a client is selected).'}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <div>
                <label className="mb-1 block text-body-xs font-medium text-on-surface">Company / Consignee Name (Ship To)</label>
                <input
                  value={shipCompanyName}
                  onChange={(e) => setShipCompanyName(e.target.value)}
                  placeholder="Company / Consignee / Delivery Name"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm font-medium text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-body-xs font-medium text-on-surface">Delivery Address / Street</label>
                <textarea
                  value={shipStreet}
                  onChange={(e) => setShipStreet(e.target.value)}
                  rows={2}
                  placeholder="Delivery address / street"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <input
                  value={shipCity}
                  onChange={(e) => setShipCity(e.target.value)}
                  placeholder="City"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
                <input
                  value={shipState}
                  onChange={(e) => setShipState(e.target.value)}
                  placeholder="State"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
                <input
                  value={shipZip}
                  onChange={(e) => setShipZip(e.target.value)}
                  placeholder="Pincode"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  value={shipPhone}
                  onChange={(e) => setShipPhone(e.target.value)}
                  placeholder="Mobile / Phone (Ship To) - optional"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
                <input
                  value={shipEmail}
                  onChange={(e) => setShipEmail(e.target.value)}
                  placeholder="Email (Ship To) - optional"
                  className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-body-sm text-on-surface focus:border-primary focus:outline-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Currency & Shipping ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 rounded-card border border-border bg-card p-5 shadow-xs">
          <h2 className="border-b border-border pb-3 text-headline-sm font-semibold text-on-surface">Currency & Freight</h2>
          {/* One dropdown, INR included: the document is in whatever currency is
              picked here. INR means no conversion; anything else asks for the
              rate to INR (live rate fetched, editable). */}
          {canChooseCredit && !accountsInvoice && (
            <SearchableSelect
              label="Credit this sale to"
              value={creditTo}
              placeholder="Automatic — the deal's salesperson, else me"
              searchPlaceholder="Search salesperson…"
              emptyText="No salesperson matches that."
              clearable
              options={creditOptions}
              onChange={setCreditTo}
            />
          )}
          <SearchableSelect
            label="Document currency"
            value={currencyConversion ? fromCurrency : 'INR'}
            options={CURRENCY_OPTIONS}
            searchPlaceholder="Search currency or code…"
            emptyText="No currency matches that."
            onChange={(code) => switchCurrency(code)}
          />
          <Select
            label="GST pattern"
            value={effectivePattern}
            disabled={accountsInvoice || forcedExport}
            onChange={(e) => setGstPattern(e.target.value as GstPattern)}
            options={GST_PATTERN_OPTIONS}
          />
          {forcedExport && !accountsInvoice && (
            <p className="-mt-1 text-body-xs text-on-surface-variant">Export — a foreign currency or a buyer outside India carries no GST.</p>
          )}
          {accountsInvoice && (
            <p className="-mt-1 text-body-xs text-on-surface-variant">GST pattern is carried from the quotation — it cannot be changed on the invoice.</p>
          )}
          {accountsInvoice && currencyTouched && (
            <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm font-medium text-warning-text">
              Currency changed: prices are in {currencyConversion ? fromCurrency : 'INR'}{currencyConversion ? ' and booked in INR at the rate below' : ''}.
              {editingStatus && editingStatus !== 'draft' ? ' This invoice is finalized — the change goes to an Admin/Superadmin for approval.' : ''}
            </p>
          )}
          {accountsInvoice && invoiceFxNote && !currencyTouched && (
            <p className={invoiceFxNote.tone === 'warning'
              ? 'rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-body-sm font-medium text-warning-text'
              : 'text-xs text-on-surface-variant'}>
              {invoiceFxNote.text}
            </p>
          )}
          {currencyConversion && (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  label={`Rate (1 ${fromCurrency} = ₹)`}
                  type="number"
                  step="any"
                  value={conversionRate}
                  onChange={(e) => {
                    setConversionRate(e.target.value);
                    if (accountsInvoice) setCurrencyTouched(true);
                  }}
                  onBlur={() => {
                    const val = Number(conversionRate);
                    if (val > 0) applyRateChange(val);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = Number(conversionRate);
                      if (val > 0) applyRateChange(val);
                    }
                  }}
                  placeholder="e.g. 83.25"
                />
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={fxFetching}
                    onClick={() => {
                      if (accountsInvoice) setCurrencyTouched(true);
                      void loadLiveRate(fromCurrency).then((rate) => {
                        if (rate) applyRateChange(rate);
                      });
                    }}
                  >
                    {fxFetching ? 'Fetching…' : 'Fetch Live Rate'}
                  </Button>
                </div>
              </div>

              {/* Automatic currency & rate synchronization status */}
              {Number(conversionRate) > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 rounded-xl border border-success/30 bg-success/10 px-3.5 py-2.5 text-body-xs text-success-text">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-success-text shrink-0" />
                    <span>
                      Active Rate: <strong>1 {fromCurrency} = ₹{Number(conversionRate)}</strong>.
                      {hasTypedAmounts() ? ` Product prices and line totals automatically calculate in ${fromCurrency}.` : ''}
                    </span>
                  </div>
                  {appliedFxRate !== null && Math.abs(Number(conversionRate) - appliedFxRate) > 0.001 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-success/30 text-success-text hover:bg-success/20"
                      onClick={() => applyRateChange(Number(conversionRate))}
                    >
                      Sync Now
                    </Button>
                  )}
                </div>
              )}

              {Number(conversionRate) > 0 && totals.grandTotal > 0 && (
                <div className="rounded-lg bg-surface-container-low px-3 py-2 text-body-xs text-on-surface-variant flex flex-col gap-0.5">
                  <div>
                    Line prices entered in <strong>{fromCurrency}</strong>. Grand Total: <strong>{fmtMoney(totals.grandTotal)}</strong>
                  </div>
                  <div>
                    ≈ INR Equivalent: <strong className="text-on-surface font-bold">{formatINR(totals.grandTotal * Number(conversionRate))}</strong> (at 1 {fromCurrency} = ₹{Number(conversionRate)}) — {accountsInvoice
                      ? <>booked in INR for GST and accounts; the printed invoice is in {fromCurrency}.</>
                      : 'the printed document shows both.'}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-1 border-t border-border pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label={`Freight / Shipping Charges (${currencyConversion ? fromCurrency : '₹'})`}
              type="number"
              min="0"
              step="any"
              value={shippingCharge}
              onChange={(e) => setShippingCharge(e.target.value)}
              placeholder="0.00"
            />
            <Input
              label={`Round Off (${currencyConversion ? fromCurrency : '₹'})`}
              type="number"
              step="any"
              value={roundOffManual ? roundOffInput : (totals.roundOff !== undefined ? String(totals.roundOff) : '')}
              onChange={(e) => {
                setRoundOffInput(e.target.value);
                setRoundOffManual(true);
              }}
              placeholder="e.g. 0.1 or -0.23 (auto-calculated)"
            />
          </div>
        </div>

        {/* ── Follow-up details (Mandatory for Quotation — Ticket 110 & 111) ─── */}
        {documentType === 'quotation' && (
          <div className="flex flex-col gap-4 rounded-card border border-primary/40 bg-card p-5 shadow-xs">
            <div className="flex items-center gap-2 w-full border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
              <CalendarClock className="h-4 w-4 text-primary" />
              <h2 className="text-headline-sm font-semibold text-on-surface">{followupRequired ? 'Follow-up Details (Mandatory)' : 'Log a new follow-up (optional)'}</h2>
            </div>
            <FollowupCaptureFields
              value={followup}
              onChange={setFollowup}
              noteRequired={followupRequired}
              dateLabel="Follow-up Date"
            />
          </div>
        )}

        {/* ── Document details ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-5 shadow-xs">
          <div className="flex items-center gap-2 w-full border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            <h2 className="text-headline-sm font-semibold text-on-surface">{documentLabel} Details</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input label="Date" type="date" value={date} onChange={(e) => { setDate(e.target.value); if (validityDays !== null && e.target.value) setValidUntil(addDaysIso(e.target.value, validityDays)); }} />
            <Input label={(documentType === 'direct_sale' || accountsInvoice) ? "Due Date" : "Valid Until"} type="date" value={validUntil} onChange={(e) => { setValidityDays(null); setValidUntil(e.target.value); }} />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-body-xs text-on-surface-variant">{(documentType === 'direct_sale' || accountsInvoice) ? 'Due in' : 'Valid for'}</span>
            {VALIDITY_CHOICES.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => pickValidity(days)}
                className={`rounded-full border px-2.5 py-0.5 text-body-xs font-medium transition-colors ${validityDays === days ? 'border-primary bg-primary text-on-primary' : 'border-outline text-on-surface-variant hover:border-primary hover:text-primary'}`}
              >
                {days} days
              </button>
            ))}
            {validityDays === null && validUntil && <span className="text-body-xs text-on-surface-variant">· custom date</span>}
          </div>
          {/* The number is allocated atomically at save time, so none is shown
              here — a preview would be a number that may never be assigned. */}
          <p className="rounded-lg bg-surface-container-low px-3 py-2 text-body-xs text-on-surface-variant">
            {isEdit
              ? <>{documentLabel} number <strong>{docNumber || '—'}</strong>. Updating keeps the same number and refreshes the system copy.</>
              : `The ${documentLabel.toLowerCase()} number is allocated when you save.`}
          </p>
        </div>

        {/* ── Customer PO (quotation / invoice / proforma / sales order) ─────────
            Saved on the pipeline record or quotation itself — see InvoicePoPanel. */}
        {(linkedRecordId || dealId || poDocId || editId) && (
          <InvoicePoPanel recordId={linkedRecordId || dealId || undefined} orderId={poDocId || editId || undefined} />
        )}

        {/* ── Terms & notes ────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-5 shadow-xs">
          <div className="flex items-center gap-2 w-full border-b border-border pb-2 text-label-sm uppercase tracking-wider text-on-surface-variant">
            <Package className="h-4 w-4 text-primary" />
            <h2 className="text-headline-sm font-semibold text-on-surface">Terms and notes</h2>
          </div>
          {/* The terms library is 86 entries, not the handful of Net-30
              placeholders this started with, so a plain <select> means
              scrolling a wall of one-liners to find "Freight charges are not
              in our scope". SearchableSelect puts the search box INSIDE the
              dropdown, so one control both filters and picks.

              `value` is deliberately always '' — this is an ACTION, not a
              selection. It appends the term to Terms & Conditions and resets, so the
              same term can be added twice and the trigger keeps reading
              "Choose a saved term…" rather than claiming one is selected. */}
          <SearchableSelect
            label="Insert from the terms library"
            value=""
            placeholder="Choose a saved term…"
            searchPlaceholder="Search terms…"
            emptyText="No term matches that."
            onChange={(id) => {
              const preset = effectiveTerms.find((t) => t._id === id);
              if (preset) setTermsText((current) => current ? `${current}\n${preset.body}` : preset.body);
            }}
            options={effectiveTerms.map((t) => ({ value: t._id, label: t.title }))}
          />
          <label className="flex flex-1 flex-col gap-1.5">
            <span className="text-body-sm font-medium text-on-surface">Terms shown on the {documentLabel.toLowerCase()}</span>
            <textarea
              value={termsText}
              onChange={(e) => setTermsText(e.target.value)}
              rows={7}
              placeholder="Delivery timeline, packaging, freight, validity, payment conditions…"
              className="flex-1 rounded-lg border border-input bg-surface px-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/60 focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
        </div>
      </div>

      {/* ── Line items ────────────────────────────────────────────────────── */}
      <LineItemsCard
        lines={lines}
        setLine={setLine}
        removeLine={removeLine}
        setLines={setLines}
        totals={totals}
        intraState={intraState}
        isInternational={isInternational}
        gstOverride={gstOverride}
        fmtMoney={fmtMoney}
        currencyConversion={currencyConversion}
        conversionRate={conversionRate === '' ? '' : Number(conversionRate) || ''}
        fromCurrency={fromCurrency}
        discountType={lineDiscountType}
        onDiscountTypeChange={(type) => {
          setLineDiscountType(type);
          setLines((previous) => previous.map((line) => ({ ...line, discountType: type })));
        }}
      />

      {/* ── Reason for editing a finalized document ──────────────────────────
          documentType === 'direct_sale' (or the Tax Invoice, accountsInvoice)
          past draft is the ONLY case this applies to — an Admin/Superadmin's
          save still applies immediately and the server never reads this
          field for them, so the box only shows for anyone else, who the
          server actually requires it from. */}
      {isEdit && editingStatus !== 'draft' && (documentType === 'direct_sale' || accountsInvoice) && !pendingApprovalNotice && !isSystemAdminUser && (
        <div className="rounded-card border border-warning/30 bg-warning/10 p-4 shadow-xs">
          <p className="flex items-center gap-1.5 text-body-sm font-semibold text-warning-text">
            <AlertTriangle className="h-4 w-4 shrink-0" /> Editing a finalized {documentLabel.toLowerCase()}
          </p>
          <p className="mt-1 text-body-xs text-warning-text">
            Only an Admin or Superadmin's change here applies immediately. Anyone else's is sent for their
            approval and does not change anything until approved.
          </p>
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="text-body-sm font-medium text-on-surface">Reason for this change</span>
            <Input
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="e.g. Client asked to add 2 more drums before dispatch"
            />
          </label>
        </div>
      )}

      {pendingApprovalNotice && (
        <div className="rounded-card border border-info/30 bg-info/10 p-4 shadow-xs">
          <p className="flex items-center gap-1.5 text-body-sm font-semibold text-info-text">
            <Check className="h-4 w-4 shrink-0" /> Submitted for approval
          </p>
          <p className="mt-1 text-body-xs text-info-text">{pendingApprovalNotice}</p>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-card p-4 shadow-xs">
        <div className="min-w-0 text-body-xs text-on-surface-variant">
          {blockingIssues.length > 0 ? (
            <ul className="flex flex-col gap-0.5">
              {blockingIssues.map((issue) => (
                <li key={issue} className="flex items-center gap-1.5 text-warning-text">
                  <AlertTriangle className="h-3 w-3 shrink-0" /> {issue}
                </li>
              ))}
            </ul>
          ) : (
            <span className="flex items-center gap-1.5 font-semibold text-success-text">
              <Check className="h-3.5 w-3.5" /> Ready to issue.
            </span>
          )}
          {warnings.length > 0 && (
            <ul className="mt-1 flex flex-col gap-0.5">
              {warnings.map((w) => (
                <li key={w} className="flex items-center gap-1.5 text-on-surface-variant">
                  <AlertTriangle className="h-3 w-3 shrink-0 opacity-60" /> {w}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(dealId ? `/pipeline/${dealId}` : listRoute)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => save(true)} disabled={!canSaveDraft}>
            {isEdit ? (saving ? 'Updating…' : 'Update') : 'Save Draft'}
          </Button>
          {documentType !== 'direct_sale' && (!isEdit || editingStatus === 'draft') && <Button onClick={() => save(false)} disabled={!canIssue} className="px-6">
            {saving ? 'Saving…' : 'Save & Issue'}
          </Button>}
        </div>
      </div>

      {floorBlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline bg-surface p-6 shadow-xl">
            <div className="flex items-center gap-2 text-warning-text">
              <AlertTriangle className="h-5 w-5" />
              <h2 className="text-headline-sm font-semibold text-on-surface">Below the minimum selling price</h2>
            </div>
            <p className="mt-2 text-body-sm text-on-surface-variant">{floorBlock.message}</p>
            {floorBlock.violations.length > 0 && (
              <ul className="mt-3 flex flex-col gap-1 rounded-xl bg-surface-container-low p-3 text-body-xs text-on-surface">
                {floorBlock.violations.map((v) => (
                  <li key={v.lineNo}>• Line {v.lineNo}: {v.reason}</li>
                ))}
              </ul>
            )}

            {canOverrideFloor ? (
              <div className="mt-4 flex flex-col gap-2">
                <label className="text-body-sm font-medium text-on-surface">
                  Reason for selling below the floor (required)
                </label>
                <textarea
                  value={floorReason}
                  onChange={(e) => setFloorReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm"
                  placeholder="e.g. matching a competitor quote approved by the sales head"
                />
                <div className="mt-2 flex items-center justify-end gap-2">
                  <Button variant="ghost" onClick={() => { setFloorBlock(null); setFloorReason(''); }}>
                    Cancel
                  </Button>
                  <Button
                    disabled={!floorReason.trim() || saving}
                    onClick={() => save(!floorBlock.pendingIssue, floorReason.trim())}
                  >
                    {saving ? 'Saving…' : floorBlock.pendingIssue ? 'Override & issue' : 'Override & save draft'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-2">
                <p className="mr-auto text-body-xs text-on-surface-variant">
                  You cannot override the floor. Ask a manager to approve this price, or raise the rate.
                </p>
                <Button variant="ghost" onClick={() => setFloorBlock(null)}>Close</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LineItemsCard({
  lines, setLine, removeLine, setLines, totals, intraState, isInternational, gstOverride,
  fmtMoney, currencyConversion, conversionRate, fromCurrency, discountType, onDiscountTypeChange,
}: {
  lines: QuotationLine[];
  setLine: (key: string, patch: Partial<QuotationLine>) => void;
  removeLine: (key: string) => void;
  setLines: React.Dispatch<React.SetStateAction<QuotationLine[]>>;
  totals: ReturnType<typeof useQuotationTotals>;
  intraState: boolean;
  isInternational: boolean;
  gstOverride?: number | null;
  fmtMoney: (amount: number) => string;
  currencyConversion: boolean;
  conversionRate: number | '';
  fromCurrency: string;
  discountType: 'percent' | 'fixed';
  onDiscountTypeChange: (type: 'percent' | 'fixed') => void;
}) {
  // ── Product search & add ─────────────────────────────────────────────────
  // Same shape as the pipeline record's ProductLinesEditor: ONE search bar
  // above the table (not one per row), so a rep sees the identical "search or
  // browse, then click to add" flow whether they're recording an enquiry or
  // quoting it. Picking a product always ADDS a line — it never overwrites
  // whatever is in an existing row — and picking the same product twice
  // increases that line's quantity instead of duplicating the row.
  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showHierarchy, setShowHierarchy] = useState(false);
  // Rate entry mode per line (by key, not index — stable across reordering).
  // Same toggle as the Sales Pipeline's product editor: Rate is always
  // STORED as the price of one whole pack, this only changes how it's typed
  // in and displayed. See shared/utils/ratePerBaseUnit.ts.
  const [rateModeByKey, setRateModeByKey] = useState<Record<string, 'pack' | 'piece' | 'base'>>({});
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { data: products = [], isLoading } = useCatalogProducts(search);
  // Before anything is typed, the dropdown browses the catalogue instead of
  // sitting empty — clicking the box is what starts browsing, not typing.
  const isBrowsing = search.trim().length === 0;

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearching(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const totalQty = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);

  // Catalogue prices are in INR. On a document billed in USD/EUR/… they are
  // converted at the document's rate as the line is added — they used to go in
  // unchanged, so a ₹82.96 price was quoted as $82.96.
  const fxRate = currencyConversion && fromCurrency !== 'INR' ? Number(conversionRate) || 0 : 0;
  const inDocCurrency = (inr: number) => (fxRate > 0 ? Math.round((inr / fxRate) * 100) / 100 : inr);

  // A product already on the document raises that line's quantity instead of
  // adding a second row. That used to happen silently: on a long quote the line
  // was out of view, so picking the product looked like it did nothing
  // (RBM/26-27/6047, 2026-10-23 — "unable to add", while each click took line
  // 9's quantity up to 10). Now it says which line, and shows it.
  const [flashKey, setFlashKey] = useState<string | null>(null);
  useEffect(() => {
    if (!flashKey) return undefined;
    document.querySelector(`[data-line-key="${flashKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setFlashKey(null), 2500);
    return () => clearTimeout(t);
  }, [flashKey]);
  const addOrRaise = (productId: string, built: QuotationLine, addQty: number) => {
    const index = lines.findIndex((l) => l.productId && l.productId === productId);
    if (index < 0) {
      setLines((ls) => [...ls, built]);
      return;
    }
    const line = lines[index];
    setLines((ls) => ls.map((l) => (l.key === line.key ? { ...l, quantity: (Number(l.quantity) || 0) + addQty } : l)));
    toast(`${line.description || 'This product'} is already on line ${index + 1} — its quantity is now ${(Number(line.quantity) || 0) + addQty}.`, { icon: 'ℹ️', duration: 5000 });
    setFlashKey(line.key);
  };

  const addFromSearch = (p: CatalogProduct) => {
    const catalogLine = lineFromProduct(p);
    const built = { ...catalogLine, unitPrice: inDocCurrency(catalogLine.unitPrice) };
    addOrRaise(p._id, built, 1);
    setSearch('');
    setIsSearching(false);
  };

  // The RBM Product Selector — the same Division → Group → Grade → Pack Size
  // picker the pipeline record form uses, so a rep chooses a product the same
  // way whether they are recording an enquiry or quoting it.
  //
  // Adding the same product twice ADDS TO the existing line rather than
  // creating a duplicate row, which is what a quotation should show: one line
  // per product, with the quantity that was actually agreed. Same rule as
  // ProductLinesEditor's handleHierarchySelect.
  const addFromHierarchy = (item: ProductHierarchyItem, qty: number, customRate?: number) => {
    const hierarchyLine = lineFromHierarchyItem(item, qty, customRate);
    // A rate typed into the picker is taken as already in the document's
    // currency; only the catalogue's own INR price is converted.
    const built = customRate !== undefined ? hierarchyLine : { ...hierarchyLine, unitPrice: inDocCurrency(hierarchyLine.unitPrice) };
    addOrRaise(item._id, built, built.quantity);
  };

  return (
    <div className="flex flex-col gap-4 rounded-card border border-border bg-card p-5 shadow-xs">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div>
          <h2 className="flex items-center gap-2 text-headline-sm font-semibold text-on-surface">
            <Package className="h-5 w-5 text-primary" /> Line Items
          </h2>
          {/* The tax treatment is derived, not typed — this line says which one
              is in force so it is never a surprise at the bottom of the page. */}
          <p className="mt-0.5 text-body-xs text-on-surface-variant">
            {isInternational ? (
              <>This is an export: <strong>no GST</strong> is charged.</>
            ) : gstOverride != null ? (
              <>SEZ / LUT supply: <strong>{gstOverride}% GST</strong> on every line, as{' '}
              <strong>{intraState ? 'CGST + SGST' : 'IGST'}</strong>.</>
            ) : (
              <>GST comes from each product&apos;s tax profile and is charged as{' '}
              <strong>{intraState ? 'CGST + SGST' : 'IGST'}</strong> for this client.</>
            )}
          </p>
        </div>
      </div>

      {/* ── Product Search & Quick Add Bar ───────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        <div ref={searchContainerRef} className="relative flex-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              className="h-11 w-full rounded-lg border border-input bg-surface pl-10 pr-10 text-body-sm text-on-surface placeholder:text-on-surface-variant/70 transition focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Search product from catalog by name, SKU, or HSN…"
              value={search}
              onFocus={() => setIsSearching(true)}
              onChange={(e) => { setSearch(e.target.value); setIsSearching(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setIsSearching(false); }}
            />
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Spinner />
              </div>
            )}
          </div>

          {/* Product dropdown — browses the catalogue on focus, narrows to a
              search once something is typed. Opens on click, not on typing. */}
          {isSearching && (
            <div className="absolute z-30 mt-1.5 max-h-[30rem] w-full overflow-y-auto rounded-xl border border-outline bg-surface p-1 shadow-xl">
              {isBrowsing && products.length > 0 && (
                <div className="px-3 py-1.5 text-body-xs font-medium text-on-surface-variant">
                  Browse the catalogue, or type to search…
                </div>
              )}
              {products.length === 0 && !isLoading ? (
                <div className="p-4 text-center text-body-sm text-on-surface-variant">
                  {isBrowsing ? 'No products in the catalogue yet.' : `No catalog products found for "${search}".`}
                  {!isBrowsing && <div className="mt-2 text-body-xs">Add the product to the catalogue before quoting it.</div>}
                </div>
              ) : (
                products.map((p) => (
                  <button
                    key={p._id}
                    type="button"
                    onClick={() => addFromSearch(p)}
                    className="group flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition hover:bg-surface-variant/80 focus:bg-surface-variant focus:outline-none"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-on-surface text-body-sm group-hover:text-primary transition truncate">
                          {p.name}
                        </span>
                        {p.sku && (
                          <span className="shrink-0 rounded bg-surface-variant px-1.5 py-0.5 text-body-xs font-mono text-on-surface-variant">
                            {p.sku}
                          </span>
                        )}
                      </div>
                      {p.hsnSac && (
                        <div className="mt-0.5 text-body-xs text-on-surface-variant">HSN {p.hsnSac}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {currencyConversion && fromCurrency !== 'INR' && Number(conversionRate) > 0 ? (
                        <>
                          <div className="text-body-sm font-bold text-primary">
                            {fmtMoney(inDocCurrency(Number(p.sellingPrice) || Number(p.mrp) || 0))}
                          </div>
                          <div className="text-data-xs text-on-surface-variant font-medium">
                            ≈ {formatINR(Number(p.sellingPrice) || Number(p.mrp) || 0)}
                          </div>
                        </>
                      ) : (
                        <div className="text-body-sm font-semibold text-on-surface">
                          {formatINR(Number(p.sellingPrice) || Number(p.mrp) || 0)}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={() => setShowHierarchy((v) => !v)}
          className={`shrink-0 h-11 gap-1.5 font-bold shadow-xs ${showHierarchy
            ? 'bg-primary text-on-primary ring-2 ring-primary/30'
            : 'border border-outline bg-surface text-primary hover:bg-surface-container-high'}`}
        >
          <Boxes className="h-4 w-4" />
          {showHierarchy ? 'Hide Product Selector' : 'RBM Product Selector'}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() => window.open('/catalog?create=true', '_blank')}
          className="shrink-0 h-11 border-dashed text-primary hover:bg-primary/5"
          title="Create or request a new product in Catalog (requires Admin approval for BDMs)"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Add / Request Product
        </Button>
      </div>

      {showHierarchy && (
        <ProductHierarchySelector
          onSelectProduct={addFromHierarchy}
          onCancel={() => setShowHierarchy(false)}
          currencyCode={currencyConversion ? fromCurrency : 'INR'}
          conversionRate={currencyConversion ? Number(conversionRate) || 1 : 1}
        />
      )}

      {lines.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-card border-2 border-dashed border-outline-variant bg-muted/40 p-8 text-center">
          <div className="rounded-full bg-primary/10 p-3 text-primary">
            <Package className="h-6 w-6" />
          </div>
          <h4 className="mt-3 text-headline-sm font-semibold text-on-surface">No Line Items Yet</h4>
          <p className="mt-1 max-w-md text-body-sm text-on-surface-variant">
            Search for products in the catalogue above. HSN, unit and GST% are filled in automatically from the approved catalogue product.
          </p>
        </div>
      ) : (
      <>
      <div className="mb-3 flex items-center justify-end gap-2">
        <span className="text-body-sm font-semibold text-on-surface">Discount mode for all products</span>
        <div className="flex overflow-hidden rounded-lg border border-outline text-xs font-bold">
          <button type="button" onClick={() => onDiscountTypeChange('percent')} className={`px-3 py-2 ${discountType === 'percent' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>%</button>
          <button type="button" onClick={() => onDiscountTypeChange('fixed')} className={`border-l border-outline px-3 py-2 ${discountType === 'fixed' ? 'bg-primary text-on-primary' : 'text-on-surface-variant'}`}>₹</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-left text-body-xs">
          <thead>
            <tr className="border-b border-border text-label-sm uppercase tracking-wider text-on-surface-variant">
              <th scope="col" className="sticky top-0 z-10 bg-muted py-2 pr-3">Product / Description</th>
              <th scope="col" className="sticky top-0 z-10 w-24 bg-muted py-2 pr-3">HSN</th>
              <th scope="col" className="sticky top-0 z-10 w-24 bg-muted py-2 pr-3 text-right">Qty</th>
              <th scope="col" className="sticky top-0 z-10 w-20 bg-muted py-2 pr-3">Unit</th>
              <th scope="col" className="sticky top-0 z-10 w-24 bg-muted py-2 pr-3">Size</th>
              <th scope="col" className="sticky top-0 z-10 w-16 bg-muted py-2 pr-3 text-right">Pack</th>
              <th scope="col" className="sticky top-0 z-10 w-28 bg-muted py-2 pr-3 text-right">Rate ({currencyConversion ? (fromCurrency === 'USD' ? '$' : fromCurrency) : '₹'})</th>
              <th scope="col" className="sticky top-0 z-10 w-20 bg-muted py-2 pr-3 text-right">Disc</th>
              <th scope="col" className="sticky top-0 z-10 w-20 bg-muted py-2 pr-3 text-right">GST %</th>
              <th scope="col" className="sticky top-0 z-10 w-32 bg-muted py-2 pr-3 text-right">Amount ({currencyConversion ? (fromCurrency === 'USD' ? '$' : fromCurrency) : '₹'})</th>
              <th scope="col" className="sticky top-0 z-10 w-10 bg-muted py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lines.map((l, i) => (
              <tr key={l.key} data-line-key={l.key}
                className={`align-top transition-colors hover:bg-muted/50 ${flashKey === l.key ? 'bg-warning/15 ring-2 ring-inset ring-warning/40' : ''}`}>
                <td className="py-2 pr-3">
                  <div className="flex flex-col gap-1.5">
                    {l.productId ? (
                      // A catalogue-picked line shows its real name read-only
                      // — same rule as ProductLinesEditor: description stays
                      // empty for it rather than duplicating the name into a
                      // second, editable field. To change the product, remove
                      // the line and search again.
                      <div className="px-0.5 py-1 text-body-sm font-semibold text-on-surface">
                        {l.description}
                      </div>
                    ) : (
                      <input
                        value={l.description}
                        onChange={(e) => setLine(l.key, { description: e.target.value })}
                        placeholder="Product name or SKU"
                        className="w-full rounded-lg border border-outline bg-surface px-2.5 py-1.5 text-body-sm font-semibold text-on-surface focus:border-primary focus:outline-none"
                      />
                    )}
                    {/* Per-line customisation note, under the product name — the
                        same field, in the same place, as the pipeline record's
                        product table (ProductLinesEditor), so the two read
                        alike. Prints under the product on the quotation,
                        proforma, invoice and challan, and shows on the
                        Dispatch picking sheet. */}
                    <input
                      value={l.notes}
                      onChange={(e) => setLine(l.key, { notes: e.target.value })}
                      placeholder="Add item notes / specs..."
                      aria-label="Item notes or specifications"
                      className="w-full rounded-md border border-outline/70 bg-surface/50 px-2 py-1 text-body-xs text-on-surface-variant placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none"
                    />
                    {packSummary(l) && (
                      <p className="rounded-card-sm bg-success/10 px-2 py-1 text-data-xs font-semibold text-success-text">
                        {packSummary(l)}
                      </p>
                    )}
                  </div>
                </td>
                <td className="py-2 pr-3">
                  <CellInput value={l.hsnSac} onChange={(v) => setLine(l.key, { hsnSac: v })} />
                </td>
                <td className="py-2 pr-3">
                  <CellInput value={String(l.quantity)} numeric onChange={(v) => setLine(l.key, { quantity: Number(v) || 0 })} />
                </td>
                <td className="py-2 pr-3">
                  <UnitPackCell
                    unit={l.unit}
                    unitPrice={l.unitPrice}
                    packSize={l.packSize}
                    onChange={(patch) => setLine(l.key, patch)}
                  />
                  {totalBaseQuantityLabel(l.quantity, l) && (
                    <p className="mt-1 text-data-xs font-semibold text-success-text">
                      Total {totalBaseQuantityLabel(l.quantity, l)}
                    </p>
                  )}
                </td>
                {/* Size — the ONE pack's own fill size ("900 ML", "210 LTR"),
                    free text since it prints straight onto the document. This
                    is what packSummary/totalBaseQuantityLabel above and
                    UnitPackCell's auto-derived container both read — without
                    it, a manually-added line has nothing to derive a
                    packaging Unit from, and the pack summary never appears. */}
                <td className="py-2 pr-3">
                  <CellInput value={l.packSize} onChange={(v) => setLine(l.key, { packSize: v })} placeholder="e.g. 900 ML" />
                </td>
                {/* Pack — pieces per pack ("20" for 900 ML x 20). Blank/1 means
                    the pack size IS the whole unit (a single drum/barrel). */}
                <td className="py-2 pr-3">
                  <CellInput value={l.qtyPerPack ? String(l.qtyPerPack) : ''} numeric onChange={(v) => setLine(l.key, { qtyPerPack: Number(v) || 0 })} />
                </td>
                <td className="py-2 pr-3">
                  {(() => {
                    const perPack = piecesPerPack(l);
                    const hasMultiPack = perPack > 1;
                    const baseUnit = baseUnitDivisor(l);
                    const hasBaseUnit = !!baseUnit;
                    const mode = (hasMultiPack || hasBaseUnit) ? (rateModeByKey[l.key] || 'pack') : 'pack';
                    const divisor = mode === 'piece' ? perPack : mode === 'base' ? (baseUnit?.qty || 1) : 1;
                    const stored = Number(l.unitPrice) || 0;
                    const displayed = mode !== 'pack' ? Math.round((stored / divisor) * 100) / 100 : stored;
                    const fxActive = currencyConversion && fromCurrency !== 'INR' && Number(conversionRate) > 0;
                    const rateVal = Number(conversionRate) || 1;
                    const inrEquivalent = Math.round(stored * rateVal * 100) / 100;
                    const sym = fromCurrency === 'USD' ? '$' : fromCurrency === 'EUR' ? '€' : fromCurrency === 'GBP' ? '£' : `${fromCurrency} `;

                    return (
                      <div className="flex flex-col gap-1">
                        <div className="relative">
                          {fxActive && (
                            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-body-xs font-bold text-primary">
                              {sym}
                            </span>
                          )}
                          <CellInput
                            className={fxActive ? 'pl-6 font-bold text-right' : ''}
                            value={String(displayed)}
                            numeric
                            onChange={(v) => {
                              const typed = Number(v) || 0;
                              const effectiveUnitPrice = mode !== 'pack' ? typed * divisor : typed;
                              if (fxActive) {
                                // User typed price in foreign currency -> sync canonical baseInrPrice
                                const canonicalInr = convertForeignToInr(effectiveUnitPrice, rateVal);
                                setLine(l.key, { unitPrice: effectiveUnitPrice, baseInrPrice: canonicalInr });
                              } else {
                                // User typed in base INR
                                setLine(l.key, { unitPrice: effectiveUnitPrice, baseInrPrice: effectiveUnitPrice });
                              }
                            }}
                          />
                        </div>
                        {(hasMultiPack || hasBaseUnit) && (
                          <select
                            value={mode}
                            onChange={(e) => setRateModeByKey((prev) => ({ ...prev, [l.key]: e.target.value as 'pack' | 'piece' | 'base' }))}
                            className="mt-0.5 w-full rounded border-0 bg-transparent text-data-xs font-medium text-on-surface-variant focus:outline-none cursor-pointer"
                          >
                            <option value="pack">Per {l.unit ? l.unit : 'Pack'}{hasMultiPack ? ` (×${perPack})` : ''}</option>
                            {hasMultiPack && (
                              <option value="piece">Per Piece{l.packSize ? ` (${l.packSize})` : ''}</option>
                            )}
                            {hasBaseUnit && (
                              <option value="base">Per {baseUnit.unit} Cost (×{baseUnit.qty})</option>
                            )}
                          </select>
                        )}
                        {fxActive && (
                          <div className="flex items-center justify-between gap-1 text-data-xs font-medium text-on-surface-variant bg-surface-container-low/80 rounded px-1.5 py-0.5">
                            <span className="text-on-surface-variant">₹ INR:</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-on-surface">
                                {formatINR(inrEquivalent)}
                              </span>
                              <button
                                type="button"
                                title="Click to enter price in INR (₹)"
                                onClick={() => {
                                  const ans = window.prompt(`Enter price in ₹ (INR) for ${l.description || 'this item'}:`, String(inrEquivalent));
                                  if (ans !== null && !isNaN(Number(ans)) && Number(ans) >= 0) {
                                    const inrNum = Number(ans);
                                    setLine(l.key, {
                                      baseInrPrice: inrNum,
                                      unitPrice: convertInrToForeign(inrNum, rateVal),
                                    });
                                  }
                                }}
                                className="text-data-xs font-semibold text-primary hover:underline cursor-pointer"
                              >
                                Edit ₹
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <p className="mt-1 text-right text-data-xs font-semibold text-success-text">
                    {ratePerBaseUnit(l.unitPrice, l)}
                  </p>
                </td>
                <td className="py-2 pr-3">
                  <CellInput
                    value={String(discountType === 'fixed' ? l.discountAmount : l.discountPercent)}
                    numeric
                    onChange={(v) => setLine(l.key, discountType === 'fixed'
                      ? { discountType: 'fixed', discountAmount: Math.max(0, Number(v) || 0) }
                      : { discountType: 'percent', discountPercent: Math.min(100, Math.max(0, Number(v) || 0)) })}
                  />
                </td>
                <td className="py-2 pr-3">
                  {isInternational || gstOverride != null
                    ? <span className="block px-2 py-1 text-right tabular-nums text-on-surface-variant">{isInternational ? 0 : gstOverride}</span>
                    : <CellInput value={String(l.gstRate)} numeric onChange={(v) => setLine(l.key, { gstRate: Number(v) || 0 })} />}
                </td>
                <td className="py-2 pr-3 text-right font-mono tabular-nums text-body-sm font-semibold text-on-surface">
                  {fmtMoney(totals.lines[i]?.total ?? 0)}
                  <span className="block text-body-xs font-normal text-on-surface-variant">
                    {fmtMoney(totals.lines[i]?.taxable ?? 0)} + tax
                  </span>
                  {currencyConversion && fromCurrency !== 'INR' && Number(conversionRate) > 0 && (
                    <span className="block text-data-xs font-medium text-on-surface-variant">
                      ≈ {formatINR((totals.lines[i]?.total ?? 0) * Number(conversionRate))}
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() => removeLine(l.key)}
                    aria-label="Remove line"
                    className="rounded p-1 text-on-surface-variant transition hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td colSpan={2} className="py-2 pr-3 text-right text-label-sm uppercase tracking-wider text-on-surface-variant">
                Total Quantity
              </td>
              <td className="py-2 pr-3 text-right font-mono tabular-nums text-body-sm font-semibold text-on-surface">
                {totalQty}
                {lines.some((line) => totalBaseQuantityLabel(line.quantity, line)) && (
                  <span className="block text-data-xs font-medium text-success-text">
                    {lines.map((line) => totalBaseQuantityLabel(line.quantity, line)).filter(Boolean).join(' + ')}
                  </span>
                )}
              </td>
              <td colSpan={8} />
            </tr>
          </tfoot>
        </table>
      </div>
      </>
      )}

      {/* ── Totals ───────────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <dl className="w-full max-w-xs space-y-1.5">
          {/* totals.subTotal is already NET of line discounts. Listing it as
              "Subtotal" with "Discount −336" under it read as the discount
              taken twice (RBM/26-27/2333: 16,464 then −336), so the rows run
              gross → discount → taxable value, each one adding up to the next. */}
          <Row label="Subtotal" value={totals.subTotal + (totals.discountTotal || 0)} fmt={fmtMoney} />
          {totals.discountTotal > 0 && <Row label="Discount" value={-totals.discountTotal} fmt={fmtMoney} />}
          {/* Freight is part of the taxable value, so it sits above the GST it is taxed with. */}
          {totals.shippingCharge > 0 && (
            <Row label="Freight / Shipping Charges" value={totals.shippingCharge} fmt={fmtMoney} />
          )}
          {(totals.shippingCharge > 0 || totals.discountTotal > 0) && (
            <Row label={!isInternational && totals.taxTotal > 0 ? 'Taxable value' : 'Net amount'}
              value={totals.subTotal + totals.shippingCharge} fmt={fmtMoney} />
          )}
          {!isInternational && totals.taxTotal > 0 && (
            intraState ? (
              <>
                <Row label="CGST" value={totals.cgstTotal} fmt={fmtMoney} />
                <Row label="SGST" value={totals.sgstTotal} fmt={fmtMoney} />
              </>
            ) : (
              <Row label="IGST" value={totals.igstTotal} fmt={fmtMoney} />
            )
          )}
          {Math.abs(totals.roundOff || 0) > 0.001 && (
            <Row label="Round Off" value={totals.roundOff || 0} fmt={fmtMoney} />
          )}
          <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-border pt-2">
            <dt className="text-label-sm uppercase tracking-wider text-on-surface-variant">Grand Total</dt>
            <dd className="font-mono tabular-nums text-right text-data-lg font-semibold text-on-surface">{fmtMoney(totals.grandTotal)}</dd>
          </div>
          {currencyConversion && Number(conversionRate) > 0 && totals.grandTotal > 0 && (
            <div className="flex items-baseline justify-between gap-3 border-t border-dashed border-border pt-1.5">
              <dt className="text-body-xs text-on-surface-variant">INR Equivalent (approx.)</dt>
              <dd className="font-mono tabular-nums text-right text-body-sm font-semibold text-on-surface-variant">{formatINR(totals.grandTotal * Number(conversionRate))}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}

function Row({ label, value, fmt }: { label: string; value: number; fmt?: (v: number) => string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-label-sm uppercase tracking-wider text-on-surface-variant">{label}</dt>
      <dd className={`font-mono tabular-nums text-right text-data-md ${value < 0 ? 'text-destructive' : 'text-on-surface'}`}>
        {fmt ? fmt(value) : formatINR(value)}
      </dd>
    </div>
  );
}

function CellInput({
  value, onChange, numeric = false, placeholder, className = '',
}: { value: string; onChange: (v: string) => void; numeric?: boolean; placeholder?: string; className?: string }) {
  const [localVal, setLocalVal] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalVal(value);
    }
  }, [value, isFocused]);

  return (
    <input
      value={isFocused ? localVal : value}
      placeholder={placeholder}
      inputMode={numeric ? 'decimal' : undefined}
      onFocus={() => {
        setIsFocused(true);
        setLocalVal(value);
      }}
      onBlur={() => {
        setIsFocused(false);
        const trimmed = localVal.trim();
        if (numeric) {
          if (trimmed === '' || trimmed === '.' || trimmed === '-') {
            onChange('0');
            setLocalVal('0');
          } else {
            const num = parseFloat(trimmed);
            if (!isNaN(num)) {
              onChange(trimmed);
            } else {
              onChange('0');
              setLocalVal('0');
            }
          }
        } else {
          onChange(trimmed);
        }
      }}
      onChange={(e) => {
        const next = e.target.value;
        if (numeric) {
          if (/^-?\d*\.?\d*$/.test(next)) {
            setLocalVal(next);
            if (next !== '' && next !== '.' && next !== '-' && !next.endsWith('.')) {
              const num = parseFloat(next);
              if (!isNaN(num)) {
                onChange(next);
              }
            } else if (next === '' || next === '.') {
              onChange('0');
            }
          }
        } else {
          setLocalVal(next);
          onChange(next);
        }
      }}
      className={`w-full rounded-lg border border-input bg-surface px-2 py-1.5 text-body-sm focus:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${numeric ? 'text-right font-mono tabular-nums' : ''} ${className}`}
    />
  );
}

export default QuotationFormPage;
